import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Save, Eye, X, AlertCircle, Camera, CheckCircle, UserPlus, Phone, MapPin, ChevronDown, Calendar, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DateInputDDMMYYYY from '../../components/DateInputDDMMYYYY/DateInputDDMMYYYY';
import { getLensGroup, getLensGroupWithSEFallback } from '../../utils/lensGroup';
import i18n from '../../i18n';

const progressiveRefusalReasonOptions = [
  'Doctor refused',
  "Don't have budget",
  'Previous bad experience',
  'Too expensive',
  'Friend or family with previous experience',
  'No information about the product'
];

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: ${props => props.theme.spacing.lg};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;


const Form = styled.form`
  max-width: 1000px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 3px solid transparent;
  border-image: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-image-slice: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

// Row 1: first name / last name / date of birth / age / address.
// Date and age hold fixed-length content, so they get fixed columns sized to
// fit rather than a share of the row: 130px covers the "dd/mm/yyyy" placeholder
// (122px, wider than the value) and the "Date of Birth" label; 70px covers
// three digits (61px). Everything left over goes to the free-text fields.
const PatientIdentityRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1.2fr 130px 70px 1.1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: start;

  /* Grid items default to min-width:auto, which lets long values push the
     row wider than its container instead of shrinking. */
  > * { min-width: 0; margin-bottom: 0; }

  /* A wrapped label pushes its input out of alignment with the rest of the
     row, so keep every label on one line. Columns are sized to fit them. */
  > * > label { white-space: nowrap; }

  /* Age is a number input; the spinner arrows eat ~17px and were clipping a
     two-digit value. The field is read-only whenever a birthday is set. */
  input[type='number'] {
    -moz-appearance: textfield;
  }
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// "What's still missing" note in the order section — the only survivor of the
// old progress checklist, shown solely when it's actionable.
const OrderHint = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.7rem;
  border-left: 3px solid #f59e0b;
  border-radius: 6px;
  background: #fffbeb;
  color: #b45309;
  font-size: 0.82rem;
  font-weight: 600;
`;

// Correction block titles ("Far Vision Correction", "Close Up Vision"). Matched
// to PhotoHeading so the two columns share one heading scale — at 1.125rem they
// competed with the section title above them.
// Far / close up lens tabs for a single-vision "Both" sale (two pairs).
const PairSwitch = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const PairTab = styled.button`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  text-align: left;
  padding: 0.7rem 0.9rem;
  border-radius: 0.6rem;
  cursor: pointer;
  border: 2px solid ${(p) => (p.$active ? '#6366f1' : '#e2e8f0')};
  background: ${(p) => (p.$active ? '#eef2ff' : 'white')};

  .label { font-weight: 700; font-size: 0.9rem; color: #1e293b; }
  .pick { font-size: 0.8rem; color: #059669; }
  .pick.missing { color: #b45309; }
`;

const RxBlockTitle = styled.h4`
  margin: 0;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 700;
`;

// The age-derived prescription-type notice and the "Special case" override sit
// on one line. Notices carry their own bottom margin, so it's zeroed here and
// the row owns the spacing instead.
const RxNoticeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem 1rem;
  margin-bottom: 1rem;

  p { margin-bottom: 0; }
`;

const SpecialCaseToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.75rem;
  padding: 0.35rem 0.7rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  background: ${props => props.theme.colors.white};
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }
`;

// ── Eye measurements: corrections and photo in one card ──────────────────────
// A single bordered surface holding both columns. They were previously two
// separate floating cards, which read as unrelated panels; the card now owns
// the chrome and the children are plain content divided by one rule. Owner
// asked for the photo to always sit under the correction rather than beside
// it, so this is a single stacked column at every width now (it used to
// switch to two side-by-side columns above 1100px).
const RxLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: 1.25rem 0;
  padding: 1.1rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
`;

const RxMainCol = styled.div`
  min-width: 0;
`;

// Divided from the correction by a rule rather than by a gap between cards, so
// the two still clearly belong to the same panel. Capped width so the photo
// doesn't stretch edge-to-edge now that it's not sharing the row with the
// correction table.
const RxPhotoCol = styled.div`
  min-width: 0;
  max-width: 420px;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
`;

// ── Previous Prescription card ───────────────────────────────────────────────
// Name, phone, type, date and doctor read as a single line separated by dots.
const PrevRxMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.3rem 0.75rem;
  margin-bottom: 0.9rem;
  color: #64748b;
  font-size: 0.875rem;

  strong { color: #1e293b; font-weight: 700; }

  /* Dot separators live in CSS so no empty <span> is needed between items,
     and they disappear correctly when an item wraps to the next line. */
  > * + *::before {
    content: '·';
    margin-right: 0.75rem;
    color: #cbd5e1;
  }
`;

// The correction tables and the photo sit side by side; the photo drops below
// only when the card gets too narrow for both.
const PrevRxBody = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrevRxTables = styled.div`
  flex: 1 1 340px;
  min-width: 0;

  h4 {
    margin: 0 0 0.4rem;
    color: #475569;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    font-size: 0.82rem;
  }

  thead tr {
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.68rem;
    text-transform: uppercase;
  }

  th, td {
    text-align: left;
    padding: 0.4rem 0.55rem;
  }

  td.eye { font-weight: 700; color: #475569; }
`;

const PrevRxPhoto = styled.div`
  flex: 0 0 150px;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.6rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;

  img {
    width: 100%;
    height: 92px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }
`;

// Row 2: phone / occupation / prescription date / prescribed by. A phone number
// is a fixed ~10 digits so it needs the least; occupation and prescriber carry
// an inline "add" button plus a chevron, and the date carries a calendar button.
const PatientContactRow = styled.div`
  display: grid;
  /* The date column is sized by its label, not its input: "Prescription Date *"
     measures ~120px at 0.875rem/600, wider than the 122px field needs. */
  grid-template-columns: 0.9fr 1.3fr 150px 1.5fr;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: start;

  > * { min-width: 0; margin-bottom: 0; }
  > * > label { white-space: nowrap; }

  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  letter-spacing: 0.2px;
`;

// Sits next to the First Name label instead of the match list popping open on
// its own — the list used to auto-open while typing/on focus and, with
// several matches, grew tall enough to cover the fields below it (Occupation,
// Prescription Date, Prescribed By). Now a match is just a quiet signal the
// salesperson can act on; the list itself is opt-in (click the badge).
const ExistingClientBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 1px solid #86efac;
  background: #f0fdf4;
  color: #15803d;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  cursor: pointer;
  animation: existingClientPulse 1.6s ease-in-out infinite;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    flex-shrink: 0;
  }

  @keyframes existingClientPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.35); }
    50% { box-shadow: 0 0 0 4px rgba(34, 197, 94, 0); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  color: #1f2937;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover:not(:focus) {
    border-color: #d1d5db;
  }

  &[type='date'],
  &[type='datetime-local'],
  &[type='month'] {
    min-height: 2.75rem;
    cursor: pointer;
  }

  &[type='date']::-webkit-calendar-picker-indicator,
  &[type='datetime-local']::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.9;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const DoctorSelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  /* Same reason as DoctorSelectField: the text input beside the "+" button
     must be allowed to shrink rather than overflow its column. */
  > input { min-width: 0; }
`;

// Red, blinking notice for the 43+ → Progressive rule, to grab the salesperson's attention.
// Pinned under the sales step bar so it stays in view while the salesperson
// scrolls the whole prescription: the progressive notice (43+) and a button
// to look the lens catalog up.
const PinnedStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.45rem 1.25rem;
  background: ${(p) => (p.$alert ? '#fef2f2' : '#f8fafc')};
  border-bottom: 1px solid ${(p) => (p.$alert ? '#fecaca' : '#e2e8f0')};
`;

const CatalogPinButton = styled.button`
  margin-inline-start: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.85rem;
  border-radius: 9999px;
  border: 1px solid #c7d2fe;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 700;
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;
`;

const ProgressiveBanner = styled.div`
  flex: 1;
  text-align: center;
  font-size: 1rem;
  font-weight: 800;
  font-style: italic;
  color: #dc2626;
  span {
    animation: blinkProgressive 1s steps(1, end) infinite;
  }
  @keyframes blinkProgressive {
    50% { opacity: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    span { animation: none; }
  }
`;

const DoctorSelectField = styled.select`
  flex: 1;
  /* A flex item's default min-width:auto sizes a <select> to its longest
     option, so a long doctor name shoved the adjacent "+" button out of the
     column. Let it shrink and ellipsise instead. */
  min-width: 0;
  text-overflow: ellipsis;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  appearance: none;
  padding-right: 2.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #1f2937;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover:not(:focus) {
    border-color: #d1d5db;
  }
`;

const DoctorSelectIcon = styled.div`
  position: absolute;
  /* Measured from the wrapper's right edge, so it must clear the 44px "+"
     button and the 0.5rem flex gap before sitting 1rem inside the select.
     The old flat 48px put it over the gap, outside the field. */
  right: calc(44px + 0.5rem + 1rem);
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #9ca3af;
`;

const AddDoctorBtn = styled.button`
  width: 44px;
  height: 44px;
  min-width: 44px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(6, 182, 212, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(6, 182, 212, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const InlineDoctorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
`;

const InlineDoctorContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  width: 90%;
  max-width: 440px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: modalSlideUp 0.3s ease-out;

  @keyframes modalSlideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const InlineDoctorTitle = styled.h3`
  color: #1e293b;
  font-size: 1.25rem;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InlineDoctorFormGroup = styled.div`
  margin-bottom: 1rem;
`;

const InlineDoctorLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const InlineDoctorInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #06b6d4;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const InlineDoctorActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const InlineDoctorBtn = styled.button`
  flex: 1;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.cancel {
    background: #f1f5f9;
    color: #475569;
    &:hover { background: #e2e8f0; }
  }

  &.save {
    background: #06b6d4;
    color: white;
    &:hover { background: #0891b2; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
`;

const InlineDoctorSuccess = styled.div`
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  padding: 0.625rem 0.875rem;
  border-radius: 10px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
`;

// Prescription-style table: OD / OG rows stacked, one column per measurement
// Plain grid — the surrounding RxLayout card supplies the border and ground,
// so nesting another bordered box here would double the chrome.
//
// Data columns are a fixed 7rem — big enough that the two 1.8rem stepper
// buttons plus their gaps still leave room for the digits — instead of
// minmax(0, 1fr): on a tablet, 4 columns (progressive's Add column showing)
// used to squeeze each cell so far that the stepper buttons ate the whole
// width and the number itself became unreadable/invisible. A fixed size
// (rather than minmax(7rem, 1fr)) also stops columns ballooning huge with
// mostly empty space on a wide screen — below it the table scrolls
// horizontally (see RxTableScroll) instead of squeezing further.
const RxTable = styled.div`
  display: grid;
  grid-template-columns: 2.75rem repeat(${props => props.$cols}, 7rem);
  gap: 0.35rem 0.6rem;
  align-items: start;
  min-width: max-content;

  @media (max-width: 640px) {
    grid-template-columns: 2.25rem repeat(${props => props.$cols}, 6rem);
    gap: 0.3rem 0.4rem;
  }
`;

// Lets the table scroll sideways instead of squeezing its columns unreadable
// when the viewport (tablet, sidebar open) is narrower than the content needs.
const RxTableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -0.25rem;
  padding: 0 0.25rem;
`;

const RxHeadCell = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  padding-bottom: 0.15rem;
`;

const RxRowLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
  align-self: start;
  padding-top: 0.75rem;
`;

// One correction cell: a value flanked by its own -/+ buttons. Chrome renders
// no spinner arrows at all on touch devices, and the Android numeric keypad has
// no minus key, which left tablet users with no way to enter a negative sphere
// or cylinder. These buttons are the only stepper on every platform, so the
// native ones are switched off below to avoid showing two sets on desktop.
const RxFieldRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.25rem;

  input[type='number'] {
    flex: 1 1 auto;
    min-width: 0;
    text-align: center;
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    font-size: 0.9rem;
    -moz-appearance: textfield;
  }
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* The lift on focus would leave the buttons behind by a pixel. */
  input[type='number']:focus { transform: none; }

  @media (max-width: 640px) {
    gap: 0.15rem;
    input[type='number'] { font-size: 0.85rem; padding-left: 0.15rem; padding-right: 0.15rem; }
  }
`;

// Sized for a fingertip, not a mouse pointer — this is the tablet's only stepper.
const RxStepButton = styled.button`
  flex: 0 0 auto;
  width: 1.8rem;
  padding: 0;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) { background: #eef2ff; border-color: #c7d2fe; }
  &:active:not(:disabled) { background: #e0e7ff; border-color: #a5b4fc; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 640px) {
    width: 1.5rem;
    font-size: 0.9rem;
  }
`;

// The transposed equivalent, shown under every field. Six saturated amber
// blocks out-shouted the inputs they annotate and read as warnings — amber
// means "caution" elsewhere in this app. Quiet, borderless, and clearly
// secondary now, with tabular figures so the columns line up.
const TranspositionValue = styled.div`
  margin-top: 0.25rem;
  padding: 0.15rem 0.3rem;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.3;
  font-weight: 600;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing['2xl']};
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CorrectionTypeContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const AlertModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const AlertModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Lookup tables for the alert color palettes — keep all three variants
// (success / warning / error) in one place so headers, icons, titles and
// buttons stay in sync.
const ALERT_PALETTES = {
  success: {
    headerGradient: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    headerBorder: '#a7f3d0',
    iconBg: '#059669',
    titleColor: '#065f46',
    buttonGradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    buttonHoverGradient: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
  },
  warning: {
    headerGradient: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    headerBorder: '#fde68a',
    iconBg: '#d97706',
    titleColor: '#92400e',
    buttonGradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    buttonHoverGradient: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)',
  },
  error: {
    headerGradient: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    headerBorder: '#fecaca',
    iconBg: '#dc2626',
    titleColor: '#991b1b',
    buttonGradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    buttonHoverGradient: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
  },
};

const palette = (type) => ALERT_PALETTES[type] || ALERT_PALETTES.error;

const AlertModalHeader = styled.div`
  padding: 1.5rem;
  background: ${props => palette(props.type).headerGradient};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid ${props => palette(props.type).headerBorder};
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => palette(props.type).iconBg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const AlertTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => palette(props.type).titleColor};
  margin: 0;
  flex: 1;
`;

const AlertModalBody = styled.div`
  padding: 1.5rem;
`;

const AlertMessage = styled.p`
  color: #1e293b;
  font-size: 0.9375rem;
  line-height: 1.5;
  margin: 0;
`;

const AlertButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: ${props => palette(props.type).buttonGradient};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;

  &:hover {
    background: ${props => palette(props.type).buttonHoverGradient};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DuplicateModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2001;
  padding: 1rem;
  animation: fadeIn 0.2s ease-out;
`;

const DuplicateModalContent = styled.div`
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease-out;
`;

const DuplicateModalHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-bottom: 1px solid #fde68a;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DuplicateModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #92400e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DuplicateModalBody = styled.div`
  padding: 1.5rem;
`;

const OldCorrectionSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const DuplicateHistoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const DuplicateHistoryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.9rem;
`;

const DuplicateHistoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 0.7rem;
`;

const DuplicateHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const DuplicateHistoryItem = styled.div`
  padding: 0.65rem;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.82rem;
  line-height: 1.45;
`;

const HistoryMeta = styled.div`
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
`;

const EmptyHistory = styled.div`
  color: #94a3b8;
  font-size: 0.82rem;
  padding: 0.75rem;
  border-radius: 10px;
  background: #f8fafc;
`;

const HistoryPhotoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.55rem;
  padding: 0.45rem 0.65rem;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #e0e7ff;
  }
`;

const HistoryPhotoOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(15, 23, 42, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const HistoryPhotoModal = styled.div`
  width: min(920px, 96vw);
  max-height: 92vh;
  overflow: auto;
  background: #ffffff;
  border-radius: 18px;
  padding: 1rem;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.35);
`;

const HistoryPhotoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
  color: #0f172a;
  font-weight: 700;
`;

const HistoryPhotoImage = styled.img`
  width: 100%;
  max-height: 76vh;
  object-fit: contain;
  border-radius: 12px;
  background: #f8fafc;
`;

const OldCorrectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
`;

const CorrectionInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const CorrectionInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  span:first-child {
    font-weight: 600;
    color: #475569;
  }
`;

const DuplicateModalActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`;

const DuplicateButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, #047857 0%, #065f46 100%);
      transform: translateY(-1px);
    }
  ` : `
    background: #f1f5f9;
    color: #475569;
    
    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const CorrectionTypeBox = styled.div`
  padding: 1.25rem 1rem;
  border: 2px solid ${props => props.selected ? '#667eea' : '#e5e7eb'};
  border-radius: 16px;
  background: ${props => props.selected
    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
    : 'white'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;
  color: ${props => props.selected ? '#667eea' : '#374151'};
  box-shadow: ${props => props.selected ? '0 4px 12px rgba(102, 126, 234, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: ${props => props.selected ? '0.05' : '0'};
    transition: opacity 0.3s;
  }

  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.25);
    
    &::before {
      opacity: 0.05;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// No chrome of its own — it lives inside the RxLayout card alongside the
// correction, so only the empty-state drop zone carries a border.
const PhotoSection = styled.div`
  padding: 0;
  background: transparent;
`;

// Empty state: the only part that should read as "something goes here".
const PhotoDropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.4rem 1rem;
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  text-align: center;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: #f1f5f9;
  }

  .icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e0f2fe;
    color: #0369a1;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

// Confirmation state — green rail instead of a full-width success banner.
const PhotoSaved = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.7rem;
  margin-bottom: 0.7rem;
  border-left: 3px solid #059669;
  border-radius: 6px;
  background: #ecfdf5;
  color: #047857;
  font-size: 0.82rem;
  font-weight: 700;
`;

const PhotoHeading = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.7rem;
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 700;
`;

const CameraContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background: ${props => props.theme.colors.black};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  display: ${props => props.show ? 'block' : 'none'};
`;

const Canvas = styled.canvas`
  display: none;
`;

const PhotoPreview = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  margin: 0 auto;
  display: block;
`;

// In a 320px column two side-by-side buttons would be cramped, so they share
// the row evenly and wrap only if a label is unusually long.
const PhotoButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.7rem;
  flex-wrap: wrap;

  > button { flex: 1 1 auto; justify-content: center; }
`;

const PhotoButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.white};
  color: ${props => props.primary ? props.theme.colors.white : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover:not(:disabled) {
    background: ${props => props.primary ? props.theme.colors.primaryDark : props.theme.colors.gray50};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const formatPreviousRxValue = (value) => (
  value === null || value === undefined || value === '' ? '—' : String(value)
);

const formatDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

const formatLensGroupDisplay = (group) => String(group || '').replace(/\/12$/, '/12+');

const formatLensGroupPairDisplay = (groupPair, fallback = '') => (
  Array.isArray(groupPair) && groupPair.length
    ? groupPair.map(formatLensGroupDisplay).join(' & ')
    : formatLensGroupDisplay(fallback)
);

const hasPreviousEyeData = (eye) => {
  if (!eye || typeof eye !== 'object') return false;
  return ['sphere', 'cylinder', 'axis', 'add', 'pd'].some(
    key => eye[key] !== null && eye[key] !== undefined && eye[key] !== ''
  );
};

const getPreviousPrescriptionBlocks = (rx = {}) => {
  if (rx.prescriptionType === 'progressive') {
    return [{
      title: 'Far vision',
      od: rx.progressiveRightEyeFar || rx.rightEyeFar || rx.rightEye,
      os: rx.progressiveLeftEyeFar || rx.leftEyeFar || rx.leftEye,
      showAdd: false
    }];
  }

  const blocks = [];
  if (rx.prescriptionType === 'single_vision') {
    if (rx.singleVisionCorrectionType === 'far') {
      blocks.push({ title: 'Far vision', od: rx.rightEyeFar || rx.rightEye, os: rx.leftEyeFar || rx.leftEye, showAdd: false });
    } else if (rx.singleVisionCorrectionType === 'closeup') {
      blocks.push({ title: 'Close-up', od: rx.rightEyeCloseUp || rx.rightEye, os: rx.leftEyeCloseUp || rx.leftEye, showAdd: false });
    } else {
      if (hasPreviousEyeData(rx.rightEyeFar) || hasPreviousEyeData(rx.leftEyeFar)) {
        blocks.push({ title: 'Far vision', od: rx.rightEyeFar, os: rx.leftEyeFar, showAdd: false });
      }
      if (hasPreviousEyeData(rx.rightEyeCloseUp) || hasPreviousEyeData(rx.leftEyeCloseUp)) {
        blocks.push({ title: 'Close-up', od: rx.rightEyeCloseUp, os: rx.leftEyeCloseUp, showAdd: false });
      }
    }
  }

  return blocks.length ? blocks : [{ title: 'Correction', od: rx.rightEye || rx.rightEyeFar, os: rx.leftEye || rx.leftEyeFar, showAdd: false }];
};

// One answer to "can the customer have it today, and if not when?".
//
// This logic existed three times — on the recommendation card, on the
// alternative card, and nowhere at all on the lens actually chosen, which is
// the one the salesperson quotes to the customer. Same shape everywhere now.
const lensAvailability = (lens) => {
  if (!lens) return null;
  // Nothing has been asked yet. Saying "not in stock" here is a claim, not a
  // fact — and paired with a dispo-derived label it produced the contradiction
  // "Not in stock · ~In-shop (same day)".
  if (lens.stockUnknown) return { text: i18n.t('prescriptionForm2.checkingStock'), color: '#64748b', ok: false };
  if (lens.inStock) {
    // Where it sits changes what the salesperson can promise. On the shelf here
    // is handed over now; at central it still arrives same-day but has to
    // travel, and saying so up front avoids a customer being told "today" and
    // then waiting for a courier.
    if (lens.stockSource === 'central') {
      return { text: i18n.t('prescriptionForm2.notInStoreStockCentral'), color: '#b45309', ok: true, warn: true };
    }
    if (lens.stockSource === 'other') {
      return { text: i18n.t('prescriptionForm2.inAnotherStoreSameDay'), color: '#b45309', ok: true, warn: true };
    }
    return { text: i18n.t('prescriptionForm2.inStoreStockSameDay'), color: '#047857', ok: true };
  }
  // A Stock-Int lens that is out restocks internally, so quoting an external
  // "3 days" — or worse its own "same day" label — would be wrong either way.
  if (lens.stockType === 'stock_ext_fallback' || lens.stockType === 'stock_int' || lens.dispo === 'stock_int') {
    return { text: i18n.t('prescriptionForm2.stockInternNotInStock'), color: '#b45309', ok: false };
  }
  const isRx = lens.stockType === 'rx';
  const label = lens.defaultDeliveryLabel || (isRx ? i18n.t('prescriptionForm2.x710Days') : i18n.t('prescriptionForm2.x3Days'));
  return { text: `${i18n.t('prescriptionForm2.notInStock')}${label}${isRx ? ` ${i18n.t('prescriptionForm2.rx')}` : ''}`, color: '#b45309', ok: false };
};

const PrescriptionForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState('error'); // 'success' or 'error'
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateMatchType, setDuplicateMatchType] = useState(null); // 'phone' | 'identity'
  const [existingPrescriptionData, setExistingPrescriptionData] = useState(null);
  const [duplicateHistory, setDuplicateHistory] = useState(null);
  const [duplicateHistoryLoading, setDuplicateHistoryLoading] = useState(false);
  const [previousPrescription, setPreviousPrescription] = useState(null);
  const [rxReused, setRxReused] = useState(false);

  // Copy the previous prescription in full into THIS visit's form — correction
  // values, prescription date, prescribing doctor, occupation (and the photo,
  // below). Everything can still be edited afterwards.
  const reusePreviousRx = () => {
    const p = previousPrescription;
    if (!p) return;
    setFormData(prev => ({
      ...prev,
      prescriptionDate: p.prescriptionDate
        ? new Date(p.prescriptionDate).toISOString().split('T')[0]
        : prev.prescriptionDate,
      prescribedBy: p.prescribedBy || prev.prescribedBy,
      prescriptionType: p.prescriptionType || prev.prescriptionType,
      singleVisionCorrectionType: p.singleVisionCorrectionType || prev.singleVisionCorrectionType,
      specialCase: p.specialCase ?? (['closeup', 'both'].includes(p.singleVisionCorrectionType) || prev.specialCase),
      progressiveRefused: p.progressiveRefused ?? prev.progressiveRefused,
      rightEye: p.rightEye || prev.rightEye,
      leftEye: p.leftEye || prev.leftEye,
      rightEyeFar: p.rightEyeFar || prev.rightEyeFar,
      leftEyeFar: p.leftEyeFar || prev.leftEyeFar,
      rightEyeCloseUp: p.rightEyeCloseUp || prev.rightEyeCloseUp,
      leftEyeCloseUp: p.leftEyeCloseUp || prev.leftEyeCloseUp,
      progressiveRightEyeFar: p.progressiveRightEyeFar || prev.progressiveRightEyeFar,
      progressiveLeftEyeFar: p.progressiveLeftEyeFar || prev.progressiveLeftEyeFar,
      progressiveRightEyeCloseUp: p.progressiveRightEyeCloseUp || prev.progressiveRightEyeCloseUp,
      progressiveLeftEyeCloseUp: p.progressiveLeftEyeCloseUp || prev.progressiveLeftEyeCloseUp,
      pupillaryDistance: p.pupillaryDistance || prev.pupillaryDistance,
      segmentHeight: p.segmentHeight || prev.segmentHeight,
      occupation: p.occupation || prev.occupation,
    }));
    // Also carry over the previous prescription photo so the salesperson doesn't
    // have to re-shoot it (they can still retake it).
    if (p.prescriptionPhoto) {
      setPrescriptionPhoto(p.prescriptionPhoto);
      setPhotoSaved(true);
    }
    setRxReused(true);
    setTimeout(() => setRxReused(false), 2500);
  };
  const [clientNameMatches, setClientNameMatches] = useState([]);
  const [clientNameMatchesLoading, setClientNameMatchesLoading] = useState(false);
  const [showClientNameMatches, setShowClientNameMatches] = useState(false);
  const suppressClientNameSearchRef = useRef(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setDuplicateMatchType(null);
    setDuplicateHistory(null);
    setDuplicateHistoryLoading(false);
    setHistoryPrescriptionPhoto(null);
  };

  const loadDuplicateHistory = async (client) => {
    const identifier = client?._id || client?.phone || client?.phoneNumber;
    if (!identifier) {
      setDuplicateHistory(null);
      return;
    }

    try {
      setDuplicateHistoryLoading(true);
      const response = await axios.get(
        `/api/visitors/client-details/${encodeURIComponent(String(identifier))}`,
        { headers: getAuthHeaders() }
      );
      setDuplicateHistory(response.data);
    } catch (error) {
      console.error('Error loading duplicate client history:', error);
      setDuplicateHistory(null);
    } finally {
      setDuplicateHistoryLoading(false);
    }
  };

  const formatModalDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
  };

  const formatEyeSummary = (eye) => {
    if (!eye) return null;
    const parts = [
      eye.sphere !== undefined && eye.sphere !== null && eye.sphere !== '' ? `Sph ${eye.sphere}` : null,
      eye.cylinder !== undefined && eye.cylinder !== null && eye.cylinder !== '' ? `Cyl ${eye.cylinder}` : null,
      eye.axis !== undefined && eye.axis !== null && eye.axis !== '' ? `Axis ${eye.axis}` : null,
      eye.add !== undefined && eye.add !== null && eye.add !== '' ? `Add ${eye.add}` : null
    ].filter(Boolean);
    return parts.length ? parts.join(' / ') : null;
  };

  const getCorrectionSummary = (rx) => {
    if (!rx) return 'No correction details saved';
    const right =
      formatEyeSummary(rx.rightEyeFar) ||
      formatEyeSummary(rx.progressiveRightEyeFar) ||
      formatEyeSummary(rx.rightEyeCloseUp) ||
      formatEyeSummary(rx.rightEye);
    const left =
      formatEyeSummary(rx.leftEyeFar) ||
      formatEyeSummary(rx.progressiveLeftEyeFar) ||
      formatEyeSummary(rx.leftEyeCloseUp) ||
      formatEyeSummary(rx.leftEye);

    if (!right && !left) return 'No correction details saved';
    return `OD: ${right || 'N/A'} | OS: ${left || 'N/A'}`;
  };

  const getOrderItemsSummary = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    if (!items.length) return 'No purchased items saved';
    return items
      .slice(0, 3)
      .map((item) => `${item.quantity || 1}x ${item.name || item.type || 'Item'}`)
      .join(', ');
  };

  const formatSignedRx = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 'N/A';
    if (Object.is(num, -0)) return '0.00';
    return num > 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
  };

  // Axis is always 0-180 degrees, so block the minus key entirely.
  const blockMinusKey = (e) => {
    if (e.key === '-' || e.key === 'Subtract') e.preventDefault();
  };

  const transposeEye = (eye) => {
    if (!eye || eye.sphere === '' || eye.sphere === null || eye.sphere === undefined) return null;
    if (eye.cylinder === '' || eye.cylinder === null || eye.cylinder === undefined) return null;
    const sphere = parseFloat(eye.sphere);
    const cylinder = parseFloat(eye.cylinder);
    const axisRaw = eye.axis === '' || eye.axis === null || eye.axis === undefined ? null : parseFloat(eye.axis);
    if (!Number.isFinite(sphere) || !Number.isFinite(cylinder) || cylinder === 0) return null;

    let axis = null;
    if (Number.isFinite(axisRaw)) {
      axis = axisRaw + 90;
      if (axis > 180) axis -= 180;
      if (axis <= 0) axis += 180;
    }

    return {
      sphere: sphere + cylinder,
      cylinder: -cylinder,
      axis
    };
  };

  const renderTranspositionValue = (eye, field) => {
    if (field === 'add') return null;
    const value = transposeEye(eye);
    if (!value) return null;

    const content = field === 'sphere'
      ? formatSignedRx(value.sphere)
      : field === 'cylinder'
        ? formatSignedRx(value.cylinder)
        : field === 'axis'
          ? (value.axis ? Math.round(value.axis) : 'N/A')
          : 'Unchanged';

    return <TranspositionValue>{content}</TranspositionValue>;
  };

  const deriveProgressiveCloseUpEye = (farEye) => {
    const sphere = parseFloat(farEye?.sphere);
    const add = parseFloat(farEye?.add);

    return {
      sphere: Number.isFinite(sphere) && Number.isFinite(add) ? (sphere + add).toFixed(2) : '',
      cylinder: farEye?.cylinder || '',
      axis: farEye?.axis || '',
      add: ''
    };
  };

  // Slot inside SalesLayout's sticky header for the pinned progressive notice.
  const [stickySlot, setStickySlot] = useState(null);
  useEffect(() => { setStickySlot(document.getElementById('sales-sticky-slot')); }, []);

  const [formData, setFormData] = useState({
    // Patient Information
    patientFirstName: '',
    patientLastName: '',
    patientName: '',
    patientBirthday: '',
    patientAge: '',
    address: '',
    phoneNumber: '',
    occupation: '',
    prescriptionDate: '',
    prescribedBy: '',

    // Prescription Details
    prescriptionType: '',
    singleVisionCorrectionType: '', // 'far', 'closeup', 'both'
    // Single-vision "special case": an under-43 client who needs more than far
    // vision. Ticking it unlocks the Close Up / Both correction chooser.
    specialCase: false,
    // Progressive refusal: a 43+ client who declines progressive lenses and
    // opts for single-vision (far / close / both) instead, with a reason.
    progressiveRefused: false,
    progressiveRefusedReason: '',
    rightEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    leftEye: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    // Single Vision - Far correction
    rightEyeFar: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    leftEyeFar: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    // Single Vision - Close Up correction
    rightEyeCloseUp: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    leftEyeCloseUp: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    // Progressive - Far and Close Up corrections
    progressiveRightEyeFar: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    progressiveLeftEyeFar: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    progressiveRightEyeCloseUp: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    progressiveLeftEyeCloseUp: {
      sphere: '',
      cylinder: '',
      axis: '',
      add: ''
    },
    progressiveFarNotAvailable: false, // Checkbox for far correction not available
    trialLens: false, // Progressive: verre d'essai — passport box + 10 calendar days
    progressiveFarNotAvailableExplanation: '', // Explanation if far correction is not available
    progressiveCloseUpNotAvailable: false,
    progressiveCloseUpNotAvailableExplanation: '',
    pupillaryDistance: '',
    segmentHeight: '',

    // Additional Information
    notes: '',
    specialInstructions: ''
  });

  useEffect(() => {
    if (suppressClientNameSearchRef.current) {
      suppressClientNameSearchRef.current = false;
      return undefined;
    }
    const query = formData.patientFirstName.trim();
    if (query.length < 2) {
      setClientNameMatches([]);
      setShowClientNameMatches(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setClientNameMatchesLoading(true);
        const response = await axios.get(`/api/visitors/clients?search=${encodeURIComponent(query)}`, {
          headers: getAuthHeaders()
        });
        const rawMatches = Array.isArray(response.data) ? response.data : [];
        // /api/visitors/clients is shared with the Clients tab, which searches
        // name AND phone/email/client-code on purpose (a receptionist typing a
        // phone number should find the client). Here the query is only ever
        // what was typed into First Name, so a hit on someone else's last name,
        // phone or email is a false "existing" flag for an unrelated person —
        // keep only rows whose first name actually contains what was typed.
        const lowerFirst = query.toLowerCase();
        const lowerLast = formData.patientLastName.trim().toLowerCase();
        const nameMatches = rawMatches.filter((m) => {
          if (!String(m.firstName || '').toLowerCase().includes(lowerFirst)) return false;
          // A shared first name (e.g. two different "Haoues") isn't enough to
          // call someone existing — once a last name has been typed too, it
          // must also match, or two unrelated people get flagged as the same.
          if (lowerLast.length >= 2) {
            return String(m.lastName || '').toLowerCase().includes(lowerLast);
          }
          return true;
        });
        // This endpoint returns one row per (person, store) — a client who
        // visited two branches is legitimately two rows there, which the
        // Clients tab wants (each branch's own book). Here it just reads as
        // the same person listed twice, so collapse rows sharing a clientKey
        // down to one, keeping whichever was seen most recently.
        const byIdentity = new Map();
        for (const m of nameMatches) {
          const identity = m.clientKey || m.visitorId || m._id;
          const existing = byIdentity.get(identity);
          if (!existing || new Date(m.lastVisit || 0) > new Date(existing.lastVisit || 0)) {
            byIdentity.set(identity, m);
          }
        }
        const matches = [...byIdentity.values()];
        // Found matches are surfaced as a quiet badge next to the label, not
        // by popping the list open on its own — see ExistingClientBadge. Only
        // force it closed when there's nothing left to show; never re-open it
        // on the salesperson's behalf just because a new match came in.
        setClientNameMatches(matches.slice(0, 8));
        if (matches.length === 0) setShowClientNameMatches(false);
      } catch (error) {
        console.error('Error searching existing clients:', error);
        setClientNameMatches([]);
        setShowClientNameMatches(false);
      } finally {
        setClientNameMatchesLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [formData.patientFirstName, formData.patientLastName]);

  const selectExistingClientMatch = async (client) => {
    const identifier = client.clientKey || client.visitorId || client._id || client.phone;
    if (!identifier) return;

    try {
      setClientNameMatchesLoading(true);
      const response = await axios.get(`/api/visitors/client-details/${encodeURIComponent(String(identifier))}`, {
        headers: getAuthHeaders()
      });
      const details = response.data || {};
      const profile = details.profile || client;
      const latestPreviousPrescription = Array.isArray(details.prescriptions) ? details.prescriptions[0] : null;
      const firstName = profile.firstName || client.firstName || '';
      const lastName = profile.lastName || client.lastName || '';
      const previousClientId = profile._id || profile.id || client.visitorId || client._id || identifier;
      const phone = profile.phone || client.phone || '';
      // Occupation and address belong to the client; take the fullest record —
      // the latest prescription — and fall back to the stored client profile.
      const address = latestPreviousPrescription?.address || profile.address || client.address || '';
      const occupation = latestPreviousPrescription?.occupation || profile.occupation || '';

      suppressClientNameSearchRef.current = true;
      setFormData(prev => ({
        ...prev,
        patientFirstName: firstName,
        patientLastName: lastName,
        patientName: `${firstName} ${lastName}`.trim() || prev.patientName,
        patientBirthday: formatDateInputValue(profile.patientBirthday || client.birthday) || prev.patientBirthday,
        patientAge: profile.patientAge || client.age || prev.patientAge,
        address: address || prev.address,
        occupation: occupation || prev.occupation,
        phoneNumber: phone ? String(phone).replace(/[^0-9]/g, '') : prev.phoneNumber,
      }));

      const visitorData = {
        gender: profile.gender || client.gender || '',
        ageGroup: profile.ageGroup || client.ageGroup || '',
        firstName,
        lastName,
        phone,
        email: profile.email || client.email || '',
        address,
        occupation,
        patientBirthday: profile.patientBirthday || client.birthday || '',
        patientAge: profile.patientAge || client.age || '',
        previousClientId,
      };
      sessionStorage.setItem('visitorData', JSON.stringify(visitorData));
      sessionStorage.setItem('returningClientData', JSON.stringify(visitorData));

      if (latestPreviousPrescription) {
        setPreviousPrescription(latestPreviousPrescription);
        sessionStorage.setItem('previousPrescription', JSON.stringify({
          clientId: previousClientId,
          prescription: latestPreviousPrescription
        }));
      } else {
        setPreviousPrescription(null);
        sessionStorage.removeItem('previousPrescription');
      }

      setShowClientNameMatches(false);
      setClientNameMatches([]);
    } catch (error) {
      console.error('Error selecting existing client:', error);
      showAlert('Could not load existing client details.');
    } finally {
      setClientNameMatchesLoading(false);
    }
  };

  const [selectedOption, setSelectedOption] = useState('');
  const [buyerChoice, setBuyerChoice] = useState({
    frameType: '',
    frameColor: '',
    frameMaterial: '',
    lensType: '',
    lensCoating: '',
    specialRequests: ''
  });
  // "Other" lens alternatives — driven by the Lens Alternatives matrix
  // (group × sphere sign). Replaces the legacy OtherLensOptions catalogue.
  // The matrix splits options into:
  //   • lensAlternativesUp   → premium upgrades (more onéreux)
  //   • lensAlternativesDown → cheaper alternatives (+ économique)
  // The salesperson picks "Other (Up)" or "Other (Down)" to switch lists.
  // `lensAlternatives` is kept as the combined fallback list for older data.
  const [lensAlternatives, setLensAlternatives] = useState([]);
  const [lensAlternativesUp, setLensAlternativesUp] = useState([]);
  const [lensAlternativesDown, setLensAlternativesDown] = useState([]);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [selectedOtherProduct, setSelectedOtherProduct] = useState(null);
  // Which sub-list of "Other" is currently visible: 'up' | 'down' | null.
  const [otherDirection, setOtherDirection] = useState(null);

  // "Choose from full catalog" browser — lets the salesperson pick any lens
  // straight from the Lens Catalog & Pricing sheet.
  const [showCatalogBrowser, setShowCatalogBrowser] = useState(false);
  const [catalogViewOnly, setCatalogViewOnly] = useState(false);
  const [fullCatalog, setFullCatalog] = useState([]);
  const [fullCatalogLoaded, setFullCatalogLoaded] = useState(false);
  const [fullCatalogLoading, setFullCatalogLoading] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogDispoFilter, setCatalogDispoFilter] = useState('all');

  // viewOnly: opened from the pinned strip to look prices up, not to choose.
  const openCatalogBrowser = async (viewOnly = false) => {
    setCatalogViewOnly(viewOnly === true);
    setShowCatalogBrowser(true);
    if (fullCatalogLoaded || fullCatalogLoading) return;
    setFullCatalogLoading(true);
    try {
      const response = await axios.get('/api/lens-catalog', { headers: getAuthHeaders() });
      setFullCatalog(response.data?.items || []);
      setFullCatalogLoaded(true);
    } catch (error) {
      console.error('Error loading lens catalog:', error);
    } finally {
      setFullCatalogLoading(false);
    }
  };

  // Turn a catalog row into the same shape the "Other" selection uses, with a
  // Dispo-based delivery window (no live stock check).
  const chooseCatalogLens = (row) => {
    // Preserve the page scroll so picking a lens doesn't jump the view.
    const keepY = window.scrollY;
    const dispoRaw = String(row.availability || row.dispo || '').toLowerCase();
    const isRx = dispoRaw.includes('rx');
    const isInt = dispoRaw.includes('int');
    const dispo = isRx ? 'rx' : isInt ? 'stock_int' : 'stock_ext';
    const delivery = isRx
      ? { label: '7–10 days', days: 10, stockType: 'rx' }
      : isInt
        ? { label: 'In-shop (same day)', days: 0, stockType: 'stock_int' }
        : { label: '3 days', days: 3, stockType: 'stock_ext' };
    const catalogId = row._id || row.id || null;
    setSelectedOtherProduct({
      name: row.name || row.commercialName || '',
      commercialName: row.commercialName || '',
      price: row.price ?? null,
      catalogId,
      indexValue: row.indexValue || row.index || null,
      catalogGroup: row.group || null,
      dispo,
      estimatedDays: delivery.days,
      stockType: delivery.stockType,
      defaultDeliveryLabel: delivery.label,
      inStock: false,
      // Nothing has been checked yet, so the badge says "checking" rather than
      // asserting "not in stock" — which, paired with a dispo-derived label,
      // produced "Not in stock · ~In-shop (same day)".
      stockUnknown: true,
      fromCatalog: true,
    });

    // Then ask for the real answer at the prescribed power and correct it.
    (async () => {
      // Check against the stronger eye — it's the one most likely to be missing
      // from stock, so it decides the delivery promise for the pair.
      const { od, os, avgSph, avgCyl } = calculatePrescriptionValues();
      const strongest = [od, os].filter(Boolean)
        .sort((a, b) => Math.abs(b.sph) - Math.abs(a.sph))[0];
      const eye = strongest || (Number.isFinite(avgSph) ? { sph: avgSph, cyl: avgCyl || 0 } : null);
      if (!catalogId && !row.name) return;
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (catalogId) params.set('catalogId', catalogId);
        else params.set('name', row.name || '');
        if (eye?.sph != null) params.set('sph', String(eye.sph));
        if (eye?.cyl != null) params.set('cyl', String(eye.cyl ?? 0));
        const { data } = await axios.get(`/api/lens-catalog/availability?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!data?.success) return;
        setSelectedOtherProduct((prev) => (prev && (prev.catalogId === catalogId)
          ? {
            ...prev,
            inStock: !!data.inStock,
            availableQty: data.availableQty || 0,
            stockSource: data.stockSource || null,
            storeQty: data.storeQty || 0,
            centralQty: data.centralQty || 0,
            stockType: data.stockType || prev.stockType,
            defaultDeliveryLabel: data.defaultDeliveryLabel || prev.defaultDeliveryLabel,
            estimatedDays: data.estimatedDays ?? prev.estimatedDays,
            sph: eye?.sph ?? null,
            cyl: eye?.cyl ?? null,
            powerChecked: data.powerChecked !== false,
            stockUnknown: false,
          }
          : prev));
      } catch {
        // Leave it as unknown rather than claiming either way.
      }
    })();
    setSelectedOption('other');
    setShowCatalogBrowser(false);
    setCatalogSearch('');
    // Restore scroll after the re-render settles.
    requestAnimationFrame(() => window.scrollTo(0, keepY));
  };
  const [lensRecommendations, setLensRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  // Single vision "Both" sells two pairs of glasses: a far pair and a close up
  // pair, each with its own lens. The lens chooser works on one pair at a time
  // (lensPair); each pair's pick is kept in pairLenses.
  const [lensPair, setLensPair] = useState('far');
  const [pairLenses, setPairLenses] = useState({ far: null, near: null });
  const isBothPairs = formData.prescriptionType === 'single_vision' && formData.singleVisionCorrectionType === 'both';
  const lensOfPick = (pick) => (!pick ? null
    : pick.selectedOption === 'professional' ? pick.selectedRecommendation
      : pick.selectedOption === 'other' ? pick.selectedOtherProduct
        : null);
  const currentPick = { selectedOption, selectedRecommendation, selectedOtherProduct, otherDirection };
  const farPick = !isBothPairs || lensPair === 'far' ? currentPick : pairLenses.far;
  const nearPick = !isBothPairs ? null : lensPair === 'near' ? currentPick : pairLenses.near;
  const bothLensesChosen = !isBothPairs || (!!lensOfPick(farPick) && !!lensOfPick(nearPick));

  // Auto-save state
  const [autoSavedPrescriptionId, setAutoSavedPrescriptionId] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Prescription photo state
  const [prescriptionPhoto, setPrescriptionPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isCapturingPhoto, setIsCapturingPhoto] = useState(false);
  const [photoSaved, setPhotoSaved] = useState(false);
  // Whether the saved photo has actually been uploaded to a prescription record.
  // When the photo is captured before the prescription exists, it is held
  // locally and uploaded during final submission instead.
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [historyPrescriptionPhoto, setHistoryPrescriptionPhoto] = useState(null);
  const photoVideoRef = useRef(null);
  const photoCanvasRef = useRef(null);
  const photoStreamRef = useRef(null);

  // Doctor dropdown state
  const [doctors, setDoctors] = useState([]);
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [newDoctorForm, setNewDoctorForm] = useState({ name: '', phoneNumber: '', address: '', city: '', country: '' });
  const [savingDoctor, setSavingDoctor] = useState(false);
  const [addDoctorSuccess, setAddDoctorSuccess] = useState('');

  // Fetch doctors for dropdown
  const fetchDoctors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/doctors', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDoctors(response.data.doctors);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  // Occupation dropdown state (shared, server-backed list — like doctors)
  const [occupations, setOccupations] = useState([]);
  const [occupationMsg, setOccupationMsg] = useState('');
  const [occOpen, setOccOpen] = useState(false);

  const fetchOccupations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/occupations', {
        headers: { Authorization: `Bearer ${token}` },
        params: { _: Date.now() }, // cache-buster so a fresh add shows immediately
      });
      if (response.data.success) setOccupations(response.data.occupations || []);
    } catch (error) {
      console.error('Error fetching occupations:', error);
    }
  };

  // Add the currently typed occupation to the shared list (POST dedupes
  // case-insensitively), then keep it selected. Mirrors the add-doctor flow.
  const addOccupation = async () => {
    const name = String(formData.occupation || '').trim();
    if (!name) { setOccupationMsg('Type an occupation first'); setTimeout(() => setOccupationMsg(''), 2500); return; }
    const exists = occupations.some((o) => (o.name || '').toLowerCase() === name.toLowerCase());
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/occupations', { name }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchOccupations();
      setFormData((prev) => ({ ...prev, occupation: name }));
      setOccupationMsg(exists ? `"${name}" already in the list` : `"${name}" added`);
      setTimeout(() => setOccupationMsg(''), 2500);
    } catch (error) {
      setOccupationMsg(error.response?.data?.message || 'Error adding occupation');
      setTimeout(() => setOccupationMsg(''), 2500);
    }
  };

  useEffect(() => {
    fetchOccupations();
  }, []);

  const handleAddDoctorInline = async () => {
    if (!newDoctorForm.name.trim()) return;
    setSavingDoctor(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/doctors', newDoctorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const savedDoctor = response.data.doctor;
        setAddDoctorSuccess(`${savedDoctor.name} added!`);
        await fetchDoctors();
        setFormData(prev => ({ ...prev, prescribedBy: savedDoctor.name }));
        setNewDoctorForm({ name: '', phoneNumber: '', address: '', city: '', country: '' });
        setTimeout(() => {
          setAddDoctorSuccess('');
          setShowAddDoctorModal(false);
        }, 1200);
      }
    } catch (error) {
      console.error('Error adding doctor:', error);
      alert('Failed to add doctor');
    } finally {
      setSavingDoctor(false);
    }
  };

  // Calculate average sph/cyl from prescription values
  const calculatePrescriptionValues = () => {
    let avgSph = null;
    let avgCyl = null;
    // Per-eye values for the active correction block. Needed so the pricing can
    // group each eye independently and charge the higher of the two (business
    // rule: when OD and OS fall in different groups, use the more expensive one).
    let od = null;
    let os = null;

    const fromEyes = (right, left) => {
      const rightSph = parseFloat(right.sphere) || 0;
      const leftSph = parseFloat(left.sphere) || 0;
      const rightCyl = parseFloat(right.cylinder) || 0;
      const leftCyl = parseFloat(left.cylinder) || 0;
      avgSph = (rightSph + leftSph) / 2;
      avgCyl = (rightCyl + leftCyl) / 2;
      od = { sph: rightSph, cyl: rightCyl };
      os = { sph: leftSph, cyl: leftCyl };
    };

    if (formData.prescriptionType === 'single_vision') {
      if (formData.singleVisionCorrectionType === 'both' && lensPair === 'near') {
        if (formData.rightEyeCloseUp.sphere && formData.leftEyeCloseUp.sphere) {
          fromEyes(formData.rightEyeCloseUp, formData.leftEyeCloseUp);
        }
      } else if ((formData.singleVisionCorrectionType === 'far' || formData.singleVisionCorrectionType === 'both') && formData.rightEyeFar.sphere && formData.leftEyeFar.sphere) {
        fromEyes(formData.rightEyeFar, formData.leftEyeFar);
      } else if (formData.singleVisionCorrectionType === 'closeup' && formData.rightEyeCloseUp.sphere && formData.leftEyeCloseUp.sphere) {
        fromEyes(formData.rightEyeCloseUp, formData.leftEyeCloseUp);
      }
    } else if (formData.prescriptionType === 'progressive') {
      // For progressive, use far vision values if available, otherwise closeup
      if (formData.progressiveRightEyeFar.sphere && formData.progressiveLeftEyeFar.sphere) {
        fromEyes(formData.progressiveRightEyeFar, formData.progressiveLeftEyeFar);
      } else if (formData.progressiveRightEyeCloseUp.sphere && formData.progressiveLeftEyeCloseUp.sphere) {
        fromEyes(formData.progressiveRightEyeCloseUp, formData.progressiveLeftEyeCloseUp);
      }
    }

    return { avgSph, avgCyl, od, os };
  };

  // Compute lens group for a pair of eye values
  const computeGroupForPair = (rightData, leftData) => {
    let rightEye = null;
    let leftEye = null;
    const rSph = parseFloat(rightData.sphere);
    const rCyl = parseFloat(rightData.cylinder) || 0;
    const lSph = parseFloat(leftData.sphere);
    const lCyl = parseFloat(leftData.cylinder) || 0;
    if (!isNaN(rSph)) rightEye = getLensGroup({ sph: rSph, cyl: rCyl });
    if (!isNaN(lSph)) leftEye = getLensGroup({ sph: lSph, cyl: lCyl });
    return { rightEye, leftEye };
  };

  // Compute groups for each applicable section
  const svFarGroups = formData.prescriptionType === 'single_vision' && (formData.singleVisionCorrectionType === 'far' || formData.singleVisionCorrectionType === 'both')
    ? computeGroupForPair(formData.rightEyeFar, formData.leftEyeFar) : { rightEye: null, leftEye: null };

  const svCloseUpGroups = formData.prescriptionType === 'single_vision' && (formData.singleVisionCorrectionType === 'closeup' || formData.singleVisionCorrectionType === 'both')
    ? computeGroupForPair(formData.rightEyeCloseUp, formData.leftEyeCloseUp) : { rightEye: null, leftEye: null };

  const progFarGroups = formData.prescriptionType === 'progressive'
    ? computeGroupForPair(formData.progressiveRightEyeFar, formData.progressiveLeftEyeFar) : { rightEye: null, leftEye: null };

  const GroupBadge = ({ group }) => {
    if (!group) return null;
    const bgColor = group.valid ? '#f0fdf4' : '#fef2f2';
    const borderColor = group.valid ? '#059669' : '#ef4444';
    const textColor = group.valid ? '#059669' : '#ef4444';
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
        padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: '700',
        background: bgColor, border: `1px solid ${borderColor}`, color: textColor, marginLeft: '0.5rem'
      }}>
        {group.label}
        <span style={{ fontWeight: '400', opacity: 0.8 }}>({group.type})</span>
      </span>
    );
  };

  // Fetch the matched lens from the new recommendation matrix.
  // Inputs: average correction → lens group + sphere sign + patient age.
  useEffect(() => {
    const fetchMatchedLens = async () => {
      if (!formData.prescriptionType) return;
      const ageNum = parseInt(formData.patientAge, 10);
      const { avgSph, avgCyl, od, os } = calculatePrescriptionValues();

      if (Number.isNaN(ageNum) || ageNum <= 0 || avgSph === null || Number.isNaN(avgSph)) {
        setLensRecommendations([]);
        setLensAlternatives([]);
        return;
      }

      const grp = getLensGroupWithSEFallback({ sph: avgSph, cyl: avgCyl || 0 });
      if (!grp || !grp.valid) {
        setLensRecommendations([]);
        setLensAlternatives([]);
        return;
      }
      const sphereSign = avgSph > 0 ? 'plus' : avgSph < 0 ? 'minus' : 'none';

      // Build the group candidates to price. Normally OD and OS land in the same
      // group and we do one lookup. When they differ, we price BOTH eyes and keep
      // the more expensive recommendation (owner rule: charge the higher price).
      const signOf = (s) => (s > 0 ? 'plus' : s < 0 ? 'minus' : 'none');
      const candidates = [];
      const addCandidate = (eye) => {
        if (!eye) return;
        const g = getLensGroupWithSEFallback({ sph: eye.sph, cyl: eye.cyl || 0 });
        if (!g || !g.valid) return;
        const sign = signOf(eye.sph);
        const key = `${g.label}|${sign}`;
        if (candidates.some((c) => c.key === key)) return;
        candidates.push({ key, group: g.label, sign, sph: eye.sph, cyl: eye.cyl || 0 });
      };
      addCandidate(od);
      addCandidate(os);
      // Fallback to the averaged group when per-eye values aren't available.
      if (candidates.length === 0) {
        candidates.push({ key: `${grp.label}|${sphereSign}`, group: grp.label, sign: sphereSign, sph: avgSph, cyl: avgCyl || 0 });
      }

      const prescriptionType = formData.progressiveRefused
        ? 'single_vision'
        : formData.prescriptionType === 'progressive'
          ? 'progressive'
          : 'single_vision';

      // For progressive prescriptions, compute the maximum ADD across both
      // eyes / both distance + closeup blocks. The add-bump rule (≥ 2.00)
      // is triggered by *any* eye having that add — using the max errs on
      // the side of upgrading, which matches the optician's chart intent.
      let maxAdd = null;
      if (prescriptionType === 'progressive') {
        const addCandidates = [
          formData.progressiveRightEyeFar?.add,
          formData.progressiveLeftEyeFar?.add,
          formData.progressiveRightEyeCloseUp?.add,
          formData.progressiveLeftEyeCloseUp?.add,
        ]
          .map((v) => parseFloat(v))
          .filter((v) => !Number.isNaN(v));
        if (addCandidates.length) maxAdd = Math.max(...addCandidates);
      }

      setLoadingRecommendations(true);
      let recommendedName = null;
      let recommendedPrice = null;
      // Group + sign of the recommendation we actually keep (the higher-priced
      // eye) — the alternatives lookup below uses these so it stays aligned.
      let altGroupLabel = grp.label;
      let altSphereSign = sphereSign;
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        // A matrix cell can list several lenses ("Optimax" or "Ylla"); the server
        // returns all of them in `matches`, each priced from the catalog (so the
        // index shown matches the catalog). We show the whole set of the winning
        // eye's cell as selectable options.
        const fetchRecosForCandidate = async (cand) => {
          const params = new URLSearchParams({
            group: cand.group,
            sphereSign: cand.sign,
            age: String(ageNum),
            prescriptionType,
          });
          if (maxAdd !== null) params.set('add', String(maxAdd));
          // Pass this eye's cell so the server returns a real central-warehouse
          // "in stock" flag for the exact (sph, cyl).
          if (cand.sph !== null && !Number.isNaN(cand.sph)) params.set('sph', String(cand.sph));
          if (cand.cyl !== null && !Number.isNaN(cand.cyl)) params.set('cyl', String(cand.cyl || 0));
          const response = await axios.get(`/api/lens-recommendation-matrix/lookup?${params.toString()}`, { headers });
          if (!(response.data?.success && response.data.row && response.data.bracket)) return null;
          const { matches, match, row, bracket } = response.data;
          const list = (Array.isArray(matches) && matches.length) ? matches : (match ? [match] : []);
          if (!list.length) return null;
          const recos = list.map((m, i) => ({
            _id: `${row._id}:${bracket._id}:${m.catalogId || m.lensName || i}`,
            // `name` stays the real/supplier name — it drives the order payload
            // and central-warehouse stock matching. `commercialName` is the
            // selling name shown to the salesperson on the recommendation card.
            name: m.lensName,
            commercialName: m.commercialName || '',
            // Index comes from the catalog, so the card always matches the catalog.
            indexValue: m.indexValue ?? null,
            description: `Group ${formatLensGroupPairDisplay(row.groupPair, cand.group)} · ${bracket.label}`,
            price: m.price ?? null,
            estimatedDays: m.estimatedDays ?? null,
            inStock: !!m.inStock,
            availableQty: m.availableQty || 0,
            stockSource: m.stockSource || null,
            storeQty: m.storeQty || 0,
            centralQty: m.centralQty || 0,
            // The power this availability answer was computed for, so the card
            // can show what it actually checked.
            sph: cand.sph ?? null,
            cyl: cand.cyl ?? null,
            powerChecked: m.powerChecked !== false,
            productId: m.productId || null,
            stockType: m.stockType || 'stock',
            defaultDeliveryLabel: m.defaultDeliveryLabel || '3 days',
            defaultDeliveryDaysMin: m.defaultDeliveryDaysMin ?? 3,
            defaultDeliveryDaysMax: m.defaultDeliveryDaysMax ?? 3,
            matrixRowId: row._id,
            ageBracketId: bracket._id,
            ageBracketLabel: bracket.label,
            groupPair: row.groupPair,
            sphereSign: row.sphereSign,
            // The specific eye group/sign we priced (for the alternatives lookup).
            groupLabel: cand.group,
            lookupSphereSign: cand.sign,
          }));
          const maxPrice = recos.reduce((mx, r) => Math.max(mx, r.price ?? -Infinity), -Infinity);
          return { cand, recos, maxPrice };
        };

        const results = (await Promise.all(candidates.map(fetchRecosForCandidate))).filter(Boolean);
        // Winning eye = the one whose best lens is most expensive (owner rule:
        // charge the higher price when OD and OS fall in different groups).
        const winner = results.reduce((best, r) => {
          if (!best) return r;
          return (r.maxPrice ?? -Infinity) > (best.maxPrice ?? -Infinity) ? r : best;
        }, null);

        if (winner && winner.recos.length) {
          // Primary = the most expensive lens in the winning cell (default select).
          const primary = winner.recos.reduce((best, r) => {
            if (!best) return r;
            return (r.price ?? -Infinity) > (best.price ?? -Infinity) ? r : best;
          }, null);
          recommendedName = primary.name;
          recommendedPrice = primary.price;
          altGroupLabel = winner.cand.group;
          altSphereSign = winner.cand.sign;
          setLensRecommendations(winner.recos);

          const stillValid = selectedRecommendation && winner.recos.some(r => r._id === selectedRecommendation._id);
          if (!stillValid) {
            setSelectedRecommendation(primary);
            if (!selectedOption) setSelectedOption('professional');
          } else {
            // Same selected lens — refresh its copy in place so the summary/total
            // stay in sync with the card when catalog price or stock changes.
            const fresh = winner.recos.find(r => r._id === selectedRecommendation._id);
            if (fresh && (
              fresh.price !== selectedRecommendation.price ||
              fresh.inStock !== selectedRecommendation.inStock ||
              fresh.estimatedDays !== selectedRecommendation.estimatedDays ||
              fresh.commercialName !== selectedRecommendation.commercialName ||
              fresh.name !== selectedRecommendation.name
            )) {
              setSelectedRecommendation(fresh);
            }
          }
        } else {
          setLensRecommendations([]);
          setSelectedRecommendation(null);
        }
      } catch (error) {
        console.error('Error fetching matched lens recommendation:', error);
        setLensRecommendations([]);
        setSelectedRecommendation(null);
      } finally {
        setLoadingRecommendations(false);
      }

      // Pre-fetch alternatives for the same (group, sphere sign) so they're
      // ready when the salesperson clicks "Other". Excludes the recommended
      // lens to avoid showing the same card twice.
      setLoadingAlternatives(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const altParams = new URLSearchParams({
          group: altGroupLabel,
          sphereSign: altSphereSign,
          prescriptionType,
        });
        if (maxAdd !== null) altParams.set('add', String(maxAdd));
        // Alternatives are configured per age zone (< 43 / 43-50 / 50-55 / 55+),
        // so the lookup needs the age to pick the right one. Without it the
        // server falls back to the row that applies to every age.
        altParams.set('age', String(ageNum));
        if (recommendedName) altParams.set('excludeName', recommendedName);
        if (recommendedPrice != null) altParams.set('referencePrice', String(recommendedPrice));
        // Same stock context as the primary recommendation.
        if (avgSph !== null && !Number.isNaN(avgSph)) altParams.set('sph', String(avgSph));
        if (avgCyl !== null && !Number.isNaN(avgCyl)) altParams.set('cyl', String(avgCyl || 0));
        const altRes = await axios.get(`/api/lens-alternatives/lookup?${altParams.toString()}`, { headers });
        const ups   = altRes.data?.alternativesUp   || [];
        const downs = altRes.data?.alternativesDown || [];
        const combined = altRes.data?.alternatives || [];
        setLensAlternativesUp(ups);
        setLensAlternativesDown(downs);
        setLensAlternatives(combined);
        // If user had picked an alt that's no longer in the new list, clear it —
        // but never clear a lens picked straight from the full catalog.
        setSelectedOtherProduct(prev => {
          if (!prev) return prev;
          if (prev.fromCatalog) return prev;
          const allNames = new Set(combined.map(a => a.name));
          return allNames.has(prev.name) ? prev : null;
        });
      } catch (error) {
        console.error('Error fetching lens alternatives:', error);
        setLensAlternatives([]);
        setLensAlternativesUp([]);
        setLensAlternativesDown([]);
      } finally {
        setLoadingAlternatives(false);
      }
    };

    fetchMatchedLens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lensPair,
    formData.prescriptionType,
    formData.singleVisionCorrectionType,
    formData.patientAge,
    formData.progressiveRefused,
    formData.rightEyeFar?.sphere,
    formData.leftEyeFar?.sphere,
    formData.rightEyeFar?.cylinder,
    formData.leftEyeFar?.cylinder,
    formData.rightEyeCloseUp?.sphere,
    formData.leftEyeCloseUp?.sphere,
    formData.rightEyeCloseUp?.cylinder,
    formData.leftEyeCloseUp?.cylinder,
    formData.progressiveRightEyeFar?.sphere,
    formData.progressiveLeftEyeFar?.sphere,
    formData.progressiveRightEyeFar?.cylinder,
    formData.progressiveLeftEyeFar?.cylinder,
    formData.progressiveRightEyeCloseUp?.sphere,
    formData.progressiveLeftEyeCloseUp?.sphere,
    formData.progressiveRightEyeCloseUp?.cylinder,
    formData.progressiveLeftEyeCloseUp?.cylinder,
    formData.progressiveRightEyeFar?.add,
    formData.progressiveLeftEyeFar?.add,
    formData.progressiveRightEyeCloseUp?.add,
    formData.progressiveLeftEyeCloseUp?.add,
  ]);

  // Auto-save prescription when professional recommendation appears
  useEffect(() => {
    // Only auto-save when recommendation first appears and form has minimum data
    if (isAutoSaving || !selectedRecommendation || selectedOption !== 'professional') {
      return;
    }

    // Check if we have minimum required data
    if (!formData.patientName || !formData.prescriptionType || !formData.prescribedBy) {
      return;
    }

    // Check prescription type specific requirements
    let hasRequiredData = false;
    if (formData.prescriptionType === 'single_vision') {
      hasRequiredData = formData.singleVisionCorrectionType &&
        ((formData.singleVisionCorrectionType === 'far' && formData.rightEyeFar?.sphere && formData.leftEyeFar?.sphere) ||
          (formData.singleVisionCorrectionType === 'closeup' && formData.rightEyeCloseUp?.sphere && formData.leftEyeCloseUp?.sphere) ||
          (formData.singleVisionCorrectionType === 'both' && formData.rightEyeFar?.sphere && formData.leftEyeFar?.sphere && formData.rightEyeCloseUp?.sphere && formData.leftEyeCloseUp?.sphere));
    } else if (formData.prescriptionType === 'progressive') {
      const hasFar = formData.progressiveRightEyeFar?.sphere && formData.progressiveLeftEyeFar?.sphere;
      const farNotAvailable = formData.progressiveFarNotAvailable && formData.progressiveFarNotAvailableExplanation;
      hasRequiredData = hasFar || farNotAvailable;
    }

    if (!hasRequiredData) {
      return;
    }

    // Debounce auto-save to avoid too many requests
    const timeoutId = setTimeout(async () => {
      // Get or create visitor ID
      let visitorId = sessionStorage.getItem('visitorId');

      // If no visitor ID exists, create visitor entry from sessionStorage data
      if (!visitorId) {
        const visitorDataStr = sessionStorage.getItem('visitorData');
        if (!visitorDataStr) {
          return; // Can't auto-save without visitor data
        }

        try {
          const visitorData = JSON.parse(visitorDataStr);
          const timeTaken = parseInt(sessionStorage.getItem('visitorTimeTaken') || '0');

          // Get sales person from localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const salesPersonId = user.id || null;

          // Create visitor entry
          const visitorResponse = await axios.post(
            '/api/visitors',
            {
              ...visitorData,
              timeTaken,
              salesPerson: salesPersonId
            },
            { headers: getAuthHeaders() }
          );

          visitorId = visitorResponse.data._id;
          sessionStorage.setItem('visitorId', visitorId);
        } catch (error) {
          console.error('Error creating visitor for auto-save:', error);
          return; // Can't proceed without visitor
        }
      }
      setIsAutoSaving(true);
      try {
        const prescriptionData = {
          ...formData,
          visitor: visitorId,
          orderType: selectedOption || undefined,
          buyerChoice: selectedOption === 'buyer' ? buyerChoice : null,
          selectedRecommendation: selectedOption === 'professional' && selectedRecommendation ? {
            id: selectedRecommendation._id,
            name: selectedRecommendation.name,
            price: selectedRecommendation.price,
            estimatedDays: selectedRecommendation.estimatedDays ?? null
          } : selectedOption === 'other' && selectedOtherProduct ? {
            id: selectedOtherProduct.catalogId || selectedOtherProduct.name,
            name: selectedOtherProduct.name,
            price: selectedOtherProduct.price ?? null,
            estimatedDays: selectedOtherProduct.estimatedDays ?? null,
            // 'up' = premium upgrade, 'down' = cheaper alternative. Drives the
            // progressive tier in analytics (a plain recommendation is Fair).
            direction: otherDirection || null
          } : null
        };

        // Check if prescription already exists (from previous auto-save)
        const existingPrescriptionId = sessionStorage.getItem('prescriptionId') || autoSavedPrescriptionId;

        if (existingPrescriptionId) {
          // Update existing prescription
          const response = await axios.put(
            `/api/prescriptions/${existingPrescriptionId}`,
            prescriptionData,
            { headers: getAuthHeaders() }
          );
          if (response.data._id) {
            setAutoSavedPrescriptionId(response.data._id);
            sessionStorage.setItem('prescriptionId', response.data._id);
          }
        } else {
          // Create new prescription
          const response = await axios.post('/api/prescriptions', prescriptionData, { headers: getAuthHeaders() });
          if (response.data._id) {
            setAutoSavedPrescriptionId(response.data._id);
            sessionStorage.setItem('prescriptionId', response.data._id);
          }
        }
      } catch (error) {
        console.error('Error auto-saving prescription:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Don't show error to user for auto-save failures, but log it
      } finally {
        setIsAutoSaving(false);
      }
    }, 2000); // Wait 2 seconds after recommendation appears

    return () => clearTimeout(timeoutId);
  }, [
    selectedRecommendation?._id,
    selectedOption,
    formData.patientName,
    formData.prescriptionType,
    formData.prescribedBy,
    formData.singleVisionCorrectionType,
    formData.rightEyeFar?.sphere,
    formData.leftEyeFar?.sphere,
    formData.rightEyeCloseUp?.sphere,
    formData.leftEyeCloseUp?.sphere,
    formData.progressiveRightEyeFar?.sphere,
    formData.progressiveLeftEyeFar?.sphere,
    formData.progressiveRightEyeCloseUp?.sphere,
    formData.progressiveLeftEyeCloseUp?.sphere,
    formData.progressiveFarNotAvailable,
    formData.progressiveFarNotAvailableExplanation,
    formData.progressiveCloseUpNotAvailable,
    formData.progressiveCloseUpNotAvailableExplanation,
    isAutoSaving
  ]);

  // Tracking order functionality removed - use Track Order page in sidebar instead

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Check visitor data from sessionStorage
    const visitorDataStr = sessionStorage.getItem('visitorData');
    const returningClientDataStr = sessionStorage.getItem('returningClientData');
    if (visitorDataStr || returningClientDataStr) {
      try {
        const visitorData = visitorDataStr ? JSON.parse(visitorDataStr) : {};
        const returningClientData = returningClientDataStr ? JSON.parse(returningClientDataStr) : {};
        const clientPrefillData = { ...visitorData, ...returningClientData };
        // Show the returning client's previous prescription immediately — as
        // soon as it's in the session (set by the returning-client lookup or a
        // name match). Kept until the flow is reset (New Visit / skip clears it),
        // so it survives re-renders instead of needing the name dropdown.
        const previousPrescriptionStr = sessionStorage.getItem('previousPrescription');
        if (previousPrescriptionStr) {
          try {
            const storedPrevious = JSON.parse(previousPrescriptionStr);
            setPreviousPrescription(storedPrevious.prescription || storedPrevious);
          } catch (_) { /* ignore malformed */ }
        }
        // Check if ageGroup is senior
        if (clientPrefillData.ageGroup === 'senior') {
          setFormData(prev => {
            if (!prev.patientAge && !prev.prescriptionType) {
              return { ...prev, prescriptionType: 'progressive' };
            }
            return prev;
          });
        }
        if (clientPrefillData.firstName || clientPrefillData.lastName || clientPrefillData.phone || clientPrefillData.patientBirthday) {
          setFormData(prev => ({
            ...prev,
            patientFirstName: clientPrefillData.firstName || prev.patientFirstName,
            patientLastName: clientPrefillData.lastName || prev.patientLastName,
            patientName: `${clientPrefillData.firstName || ''} ${clientPrefillData.lastName || ''}`.trim() || prev.patientName,
            patientBirthday: clientPrefillData.patientBirthday
              ? new Date(clientPrefillData.patientBirthday).toISOString().split('T')[0]
              : prev.patientBirthday,
            patientAge: clientPrefillData.patientAge || prev.patientAge,
            address: clientPrefillData.address || prev.address,
            occupation: clientPrefillData.occupation || prev.occupation,
            phoneNumber: clientPrefillData.phone ? String(clientPrefillData.phone).replace(/[^0-9]/g, '') : prev.phoneNumber,
          }));
        }
        sessionStorage.removeItem('returningClientData');
      } catch (err) {
        console.error('Error parsing visitor data:', err);
      }
    }

    // Load existing prescription data if available (from incomplete order)
    const existingPrescriptionStr = sessionStorage.getItem('existingPrescription');
    if (existingPrescriptionStr) {
      try {
        const existingPrescription = JSON.parse(existingPrescriptionStr);

        // Pre-fill form with existing prescription data
        setFormData(prev => ({
          ...prev,
          // Use the stored first/last when the record has them; splitting the
          // joined name is only a fallback for older records that don't.
          patientFirstName: existingPrescription.patientFirstName
            || (existingPrescription.patientName ? existingPrescription.patientName.split(' ')[0] : prev.patientFirstName),
          patientLastName: existingPrescription.patientLastName
            || (existingPrescription.patientName ? existingPrescription.patientName.split(' ').slice(1).join(' ') : prev.patientLastName),
          patientName: existingPrescription.patientName || prev.patientName,
          patientAge: existingPrescription.patientAge || prev.patientAge,
          patientBirthday: existingPrescription.patientBirthday
            ? new Date(existingPrescription.patientBirthday).toISOString().split('T')[0]
            : prev.patientBirthday,
          address: existingPrescription.address || prev.address,
          phoneNumber: existingPrescription.phoneNumber || prev.phoneNumber,
          prescriptionDate: existingPrescription.prescriptionDate
            ? new Date(existingPrescription.prescriptionDate).toISOString().split('T')[0]
            : prev.prescriptionDate,
          prescribedBy: existingPrescription.prescribedBy || prev.prescribedBy,
          occupation: existingPrescription.occupation || prev.occupation,
          prescriptionType: existingPrescription.prescriptionType || prev.prescriptionType,
          singleVisionCorrectionType: existingPrescription.singleVisionCorrectionType || prev.singleVisionCorrectionType,
          // Re-derive the "special case" flag so an under-43 record saved with
          // close up / both re-opens the correction chooser on edit.
          specialCase: existingPrescription.specialCase ??
            (['closeup', 'both'].includes(existingPrescription.singleVisionCorrectionType) || prev.specialCase),
          progressiveRefused: existingPrescription.progressiveRefused ?? prev.progressiveRefused,
          progressiveRefusedReason: existingPrescription.progressiveRefusedReason || prev.progressiveRefusedReason,
          rightEye: existingPrescription.rightEye || prev.rightEye,
          leftEye: existingPrescription.leftEye || prev.leftEye,
          rightEyeFar: existingPrescription.rightEyeFar || prev.rightEyeFar,
          leftEyeFar: existingPrescription.leftEyeFar || prev.leftEyeFar,
          rightEyeCloseUp: existingPrescription.rightEyeCloseUp || prev.rightEyeCloseUp,
          leftEyeCloseUp: existingPrescription.leftEyeCloseUp || prev.leftEyeCloseUp,
          progressiveRightEyeFar: existingPrescription.progressiveRightEyeFar || prev.progressiveRightEyeFar,
          progressiveLeftEyeFar: existingPrescription.progressiveLeftEyeFar || prev.progressiveLeftEyeFar,
          progressiveRightEyeCloseUp: existingPrescription.progressiveRightEyeCloseUp || prev.progressiveRightEyeCloseUp,
          progressiveLeftEyeCloseUp: existingPrescription.progressiveLeftEyeCloseUp || prev.progressiveLeftEyeCloseUp,
          progressiveFarNotAvailable: existingPrescription.progressiveFarNotAvailable || prev.progressiveFarNotAvailable,
          trialLens: existingPrescription.trialLens || prev.trialLens,
          progressiveFarNotAvailableExplanation: existingPrescription.progressiveFarNotAvailableExplanation || prev.progressiveFarNotAvailableExplanation,
          progressiveCloseUpNotAvailable: existingPrescription.progressiveCloseUpNotAvailable || prev.progressiveCloseUpNotAvailable,
          progressiveCloseUpNotAvailableExplanation: existingPrescription.progressiveCloseUpNotAvailableExplanation || prev.progressiveCloseUpNotAvailableExplanation,
          pupillaryDistance: existingPrescription.pupillaryDistance || prev.pupillaryDistance,
          segmentHeight: existingPrescription.segmentHeight || prev.segmentHeight,
          notes: existingPrescription.notes || prev.notes,
          specialInstructions: existingPrescription.specialInstructions || prev.specialInstructions
        }));

        // If prescription has a photo, set it
        if (existingPrescription.prescriptionPhoto) {
          setPrescriptionPhoto(existingPrescription.prescriptionPhoto);
          setPhotoSaved(true);
        }

        // Clear the existing prescription from sessionStorage after loading
        sessionStorage.removeItem('existingPrescription');
      } catch (err) {
        console.error('Error loading existing prescription:', err);
      }
    } else {
      // Load saved form data from sessionStorage if no existing prescription
      const savedFormDataStr = sessionStorage.getItem('prescriptionFormData');
      if (savedFormDataStr) {
        try {
          const savedFormData = JSON.parse(savedFormDataStr);
          setFormData(prev => ({ ...prev, ...savedFormData }));
        } catch (err) {
          console.error('Error loading saved form data:', err);
        }
      }
    }

    const savedOrderStateStr = sessionStorage.getItem('prescriptionOrderState');
    if (savedOrderStateStr) {
      try {
        const savedOrderState = JSON.parse(savedOrderStateStr);
        setSelectedOption(savedOrderState.selectedOption || '');
        setBuyerChoice(prev => ({ ...prev, ...(savedOrderState.buyerChoice || {}) }));
        setSelectedOtherProduct(savedOrderState.selectedOtherProduct || null);
        setSelectedRecommendation(savedOrderState.selectedRecommendation || null);
        setOtherDirection(savedOrderState.otherDirection || null);
      } catch (err) {
        console.error('Error loading saved prescription order state:', err);
      }
    }

    const savedPhotoStateStr = sessionStorage.getItem('prescriptionPhotoState');
    if (savedPhotoStateStr) {
      try {
        const savedPhotoState = JSON.parse(savedPhotoStateStr);
        if (savedPhotoState.photoPreview) setPhotoPreview(savedPhotoState.photoPreview);
        if (savedPhotoState.prescriptionPhoto) setPrescriptionPhoto(savedPhotoState.prescriptionPhoto);
        if (savedPhotoState.photoSaved) setPhotoSaved(true);
      } catch (err) {
        console.error('Error loading saved prescription photo state:', err);
      }
    }
  }, []);

  // Auto-save form data to sessionStorage whenever it changes
  useEffect(() => {
    // Don't save if form is empty (initial state)
    if (formData.patientName || formData.prescriptionType) {
      sessionStorage.setItem('prescriptionFormData', JSON.stringify(formData));
    }
  }, [formData]);

  useEffect(() => {
    if (selectedOption || selectedOtherProduct || selectedRecommendation || Object.values(buyerChoice).some(Boolean)) {
      sessionStorage.setItem('prescriptionOrderState', JSON.stringify({
        selectedOption,
        buyerChoice,
        selectedOtherProduct,
        selectedRecommendation,
        otherDirection
      }));
    } else {
      sessionStorage.removeItem('prescriptionOrderState');
    }
  }, [selectedOption, buyerChoice, selectedOtherProduct, selectedRecommendation, otherDirection]);

  useEffect(() => {
    if (photoPreview || prescriptionPhoto || photoSaved) {
      try {
        sessionStorage.setItem('prescriptionPhotoState', JSON.stringify({
          photoPreview,
          prescriptionPhoto,
          photoSaved
        }));
      } catch (err) {
        // A full-resolution data URL can exceed the sessionStorage quota. Losing
        // the persisted copy is fine (state stays in memory) — just don't throw.
        console.warn('Could not persist prescription photo state:', err);
      }
    }
  }, [photoPreview, prescriptionPhoto, photoSaved]);

  useEffect(() => {
    if (!formData.progressiveRefused) return;
    if (selectedOption === 'professional' || otherDirection === 'up') {
      setSelectedOption('other');
      setOtherDirection('down');
      setSelectedRecommendation(null);
      setSelectedOtherProduct(null);
    }
  }, [formData.progressiveRefused, selectedOption, otherDirection]);

  // Prescription type by age: under 43 → single vision only; 43+ → progressive only
  useEffect(() => {
    const age = parseInt(formData.patientAge, 10);
    const hasValidAge = !isNaN(age) && age > 0;

    if (!hasValidAge) {
      return;
    }

    // A 43+ client normally locks to progressive — unless they explicitly
    // refused progressive, in which case we keep single-vision mode.
    if (age >= 43 && !formData.progressiveRefused && formData.prescriptionType !== 'progressive') {
      setFormData(prev => ({
        ...prev,
        prescriptionType: 'progressive',
        singleVisionCorrectionType: ''
      }));
    } else if (age < 43 && formData.prescriptionType !== 'single_vision') {
      setFormData(prev => ({
        ...prev,
        prescriptionType: 'single_vision',
        // Under 43 defaults to far vision only; "special case" unlocks the rest.
        singleVisionCorrectionType: 'far',
        specialCase: false
      }));
    }
  }, [formData.patientAge, formData.prescriptionType, formData.progressiveRefused]);

  useEffect(() => {
    if (formData.prescriptionType !== 'progressive') return;

    setFormData(prev => {
      const nextRightClose = deriveProgressiveCloseUpEye(prev.progressiveRightEyeFar);
      const nextLeftClose = deriveProgressiveCloseUpEye(prev.progressiveLeftEyeFar);
      const unchanged =
        prev.progressiveRightEyeCloseUp.sphere === nextRightClose.sphere &&
        prev.progressiveRightEyeCloseUp.cylinder === nextRightClose.cylinder &&
        prev.progressiveRightEyeCloseUp.axis === nextRightClose.axis &&
        prev.progressiveRightEyeCloseUp.add === '' &&
        prev.progressiveLeftEyeCloseUp.sphere === nextLeftClose.sphere &&
        prev.progressiveLeftEyeCloseUp.cylinder === nextLeftClose.cylinder &&
        prev.progressiveLeftEyeCloseUp.axis === nextLeftClose.axis &&
        prev.progressiveLeftEyeCloseUp.add === '';

      if (unchanged) return prev;

      return {
        ...prev,
        progressiveCloseUpNotAvailable: false,
        progressiveCloseUpNotAvailableExplanation: '',
        progressiveRightEyeCloseUp: nextRightClose,
        progressiveLeftEyeCloseUp: nextLeftClose
      };
    });
  }, [
    formData.prescriptionType,
    formData.progressiveRightEyeFar.sphere,
    formData.progressiveRightEyeFar.cylinder,
    formData.progressiveRightEyeFar.axis,
    formData.progressiveRightEyeFar.add,
    formData.progressiveLeftEyeFar.sphere,
    formData.progressiveLeftEyeFar.cylinder,
    formData.progressiveLeftEyeFar.axis,
    formData.progressiveLeftEyeFar.add
  ]);

  // A 43+ client who refused progressive gets single vision "Both": the close
  // up rows follow far vision the same way (sphere + add, same cyl and axis).
  // A near field follows only while it is empty or still shows the value this
  // effect last put there, so a typed value — or one loaded from a saved
  // prescription — is kept. Clearing the field makes it follow again.
  const svNearAutoRef = useRef({ rightEyeCloseUp: {}, leftEyeCloseUp: {} });
  useEffect(() => {
    if (!formData.progressiveRefused || formData.prescriptionType !== 'single_vision'
      || formData.singleVisionCorrectionType !== 'both') return;
    const patch = {};
    [['rightEyeFar', 'rightEyeCloseUp'], ['leftEyeFar', 'leftEyeCloseUp']].forEach(([farKey, nearKey]) => {
      const derived = deriveProgressiveCloseUpEye(formData[farKey]);
      const last = svNearAutoRef.current[nearKey];
      ['sphere', 'cylinder', 'axis'].forEach((field) => {
        const current = formData[nearKey][field] || '';
        // Matching the far values already (a reopened prescription) counts as following.
        if (current !== '' && current !== last[field] && current !== derived[field]) return;
        last[field] = derived[field];
        if (current !== derived[field]) patch[nearKey] = { ...patch[nearKey], [field]: derived[field] };
      });
    });
    if (!Object.keys(patch).length) return;
    setFormData((prev) => ({
      ...prev,
      ...Object.fromEntries(Object.entries(patch).map(([key, fields]) => [key, { ...prev[key], ...fields }]))
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.progressiveRefused,
    formData.prescriptionType,
    formData.singleVisionCorrectionType,
    formData.rightEyeFar.sphere,
    formData.rightEyeFar.cylinder,
    formData.rightEyeFar.axis,
    formData.rightEyeFar.add,
    formData.leftEyeFar.sphere,
    formData.leftEyeFar.cylinder,
    formData.leftEyeFar.axis,
    formData.leftEyeFar.add,
    formData.rightEyeCloseUp.sphere,
    formData.rightEyeCloseUp.cylinder,
    formData.rightEyeCloseUp.axis,
    formData.leftEyeCloseUp.sphere,
    formData.leftEyeCloseUp.cylinder,
    formData.leftEyeCloseUp.axis
  ]);

  // Check for duplicate client by phone number
  const checkDuplicateClient = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 4) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/visitors/check-duplicate?phone=${phoneNumber}`, {
        headers: getAuthHeaders()
      });

      if (response.data.exists && response.data.client) {
        setExistingPrescriptionData([response.data.client]);
        setDuplicateMatchType('phone');
        setShowDuplicateModal(true);
        loadDuplicateHistory(response.data.client);
      }
    } catch (error) {
      console.error('Error checking duplicate client:', error);
    } finally {
      setLoading(false);
    }
  };

  // Same full name + date of birth as an existing prescription
  const checkDuplicateByIdentity = async (patientName, birthday) => {
    const trimmed = (patientName || '').trim();
    if (!trimmed || trimmed.length < 2 || !birthday) return;

    try {
      setLoading(true);
      const response = await axios.get('/api/visitors/check-duplicate-identity', {
        headers: getAuthHeaders(),
        params: {
          patientName: trimmed,
          birthday
        }
      });

      if (response.data.exists && response.data.client) {
        setExistingPrescriptionData([response.data.client]);
        setDuplicateMatchType('identity');
        setShowDuplicateModal(true);
        loadDuplicateHistory(response.data.client);
      }
    } catch (error) {
      console.error('Error checking duplicate by name and date of birth:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseExistingClient = () => {
    if (!existingPrescriptionData || existingPrescriptionData.length === 0) return;

    const client = existingPrescriptionData[0];

    // Pre-fill form with client data, but START A NEW VISIT
    setFormData(prev => ({
      ...prev,
      patientName: client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : prev.patientName,
      patientAge: prev.patientBirthday ? calculateAge(prev.patientBirthday) : prev.patientAge,
      address: client.address || prev.address,
      phoneNumber: client.phone ? String(client.phone).replace(/[^0-9]/g, '') : prev.phoneNumber
    }));

    setPhotoPreview(null);
    setPrescriptionPhoto(null);
    setPhotoSaved(false);
    setPhotoUploaded(false);
    sessionStorage.removeItem('prescriptionPhotoState');
    sessionStorage.removeItem('hasPrescriptionPhoto');
    sessionStorage.removeItem('prescriptionOrderState');

    // If there's previous prescription data associated (we might want to fetch it fully here if needed)
    // For now, valid requirement is just "Start New Visit" with known identity

    closeDuplicateModal();
    showAlert(t('prescriptionForm.alerts.clientFound', { name: client.firstName }), 'success');
  };



  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Validate date of birth on blur
    if (name === 'patientBirthday' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        showAlert(t('prescriptionForm.alerts.dobFuture'));
        setFormData(prev => ({
          ...prev,
          patientBirthday: '',
          patientAge: ''
        }));
        return;
      }

      // Calculate age and validate it's realistic (0-120 years)
      const calculatedAge = calculateAge(value);
      const age = parseInt(calculatedAge);

      if (isNaN(age) || age < 0) {
        showAlert(t('prescriptionForm.alerts.dobInvalid'));
        setFormData(prev => ({
          ...prev,
          patientBirthday: '',
          patientAge: ''
        }));
        return;
      }

      if (age > 120) {
        showAlert(t('prescriptionForm.alerts.ageMax'));
        setFormData(prev => ({
          ...prev,
          patientBirthday: '',
          patientAge: ''
        }));
        return;
      }

    }

    // Validate prescription date on blur
    if (name === 'prescriptionDate' && value) {
      // Compare as local date strings to avoid UTC timezone shift
      const selectedDate = value.slice(0, 10);
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

      // Check if date is in the future
      if (selectedDate > todayStr) {
        showAlert(t('prescriptionForm.alerts.rxDateFuture'));
        setFormData(prev => ({
          ...prev,
          prescriptionDate: ''
        }));
        return;
      }

      // Date is older than 2 years — show a non-blocking warning but keep
      // the value. Prescriptions typically expire after 1-2 years, so the
      // salesperson should be aware, but we let them proceed (e.g. the
      // patient may have an older prescription on file).
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      twoYearsAgo.setHours(0, 0, 0, 0);

      if (selectedDate < twoYearsAgo) {
        showAlert(
          t('prescriptionForm.alerts.rxOld'),
          'warning'
        );
        // Intentionally do NOT clear prescriptionDate — old dates are allowed.
      }
    }

    // Validate vision correction numbers on blur
    if (name.includes('.')) {
      const [parent, child] = name.split('.');

      if (child === 'sphere' && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < -20.00 || numValue > 20.00) {
          showAlert(t('prescriptionForm.alerts.sphereRange'));
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              sphere: ''
            }
          }));
          return;
        }
        // Round to nearest 0.25
        const rounded = Math.round(numValue * 4) / 4;
        if (rounded !== numValue) {
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              sphere: rounded.toFixed(2)
            }
          }));
        }
      }

      if (child === 'cylinder' && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < -6.00 || numValue > 6.00) {
          showAlert(t('prescriptionForm.alerts.cylinderRange'));
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              cylinder: ''
            }
          }));
          return;
        }
        // Round to nearest 0.25
        const rounded = Math.round(numValue * 4) / 4;
        if (rounded !== numValue) {
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              cylinder: rounded.toFixed(2)
            }
          }));
        }
      }

      // Axis only makes sense with a cylinder — clear it once cylinder is removed.
      if (child === 'cylinder' && (value === '' || parseFloat(value) === 0)) {
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            axis: ''
          }
        }));
      }

      if (child === 'axis' && value !== '') {
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 0 || numValue > 180) {
          showAlert(t('prescriptionForm.alerts.axisRange'));
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              axis: ''
            }
          }));
          return;
        }
      }

      if (child === 'add' && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0.00 || numValue > 4.00) {
          showAlert(t('prescriptionForm.alerts.addRange'));
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              add: ''
            }
          }));
          return;
        }
        // Round to nearest 0.25
        const rounded = Math.round(numValue * 4) / 4;
        if (rounded !== numValue) {
          setFormData(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              add: rounded.toFixed(2)
            }
          }));
        }
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };

        // Auto-calculate age when birthday changes (validation happens on blur)
        if (name === 'patientBirthday') {
          const calculatedAge = calculateAge(value);
          newData.patientAge = calculatedAge;
          const ageNum = parseInt(calculatedAge, 10);
          if (!isNaN(ageNum) && ageNum > 0) {
            if (ageNum >= 43) {
              // Keep single-vision mode if the client refused progressive.
              if (!newData.progressiveRefused) {
                newData.prescriptionType = 'progressive';
                newData.singleVisionCorrectionType = '';
              }
            } else {
              newData.prescriptionType = 'single_vision';
              newData.singleVisionCorrectionType = 'far';
              newData.specialCase = false;
              newData.progressiveRefused = false;
              newData.progressiveRefusedReason = '';
            }
          }
        }

        if (name === 'prescriptionType') {
          const age = parseInt(newData.patientAge || formData.patientAge, 10);
          const hasValidAge = !isNaN(age) && age > 0;
          if (hasValidAge && age >= 43 && value !== 'progressive') {
            showAlert(t('prescriptionForm.alerts.mustProgressive'));
            return prev;
          }
          if (hasValidAge && age < 43 && value !== 'single_vision') {
            showAlert(t('prescriptionForm.alerts.mustSingle'));
            return prev;
          }

          if (value !== 'single_vision') {
            newData.singleVisionCorrectionType = '';
          }
        }

        return newData;
      });
    }
  };

  const showAlert = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
  };

  const getSelectedProgressiveRefusalReasons = (value = formData.progressiveRefusedReason) => (
    String(value || '')
      .split(';')
      .map((reason) => reason.trim())
      .filter(Boolean)
  );

  const toggleProgressiveRefusalReason = (reason) => {
    setFormData(prev => {
      const reasons = getSelectedProgressiveRefusalReasons(prev.progressiveRefusedReason);
      const nextReasons = reasons.includes(reason)
        ? reasons.filter((item) => item !== reason)
        : [...reasons, reason];
      return {
        ...prev,
        progressiveRefusedReason: nextReasons.join('; ')
      };
    });
  };

  const closeAlert = () => {
    setAlertMessage(null);
  };

  const calculateAge = (birthday) => {
    if (!birthday) return '';
    const today = new Date();
    const birthDate = new Date(birthday);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age.toString();
  };

  // Tracking order functionality removed - use Track Order page in sidebar instead

  useEffect(() => {
    return () => {
      // Cleanup photo camera on unmount
      if (photoStreamRef.current) {
        photoStreamRef.current.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        photoStreamRef.current = null;
      }
      if (photoVideoRef.current) {
        photoVideoRef.current.pause();
        photoVideoRef.current.srcObject = null;
      }
    };
  }, []);

  // Photo capture functions
  const startPhotoCapture = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent multiple simultaneous camera requests
    if (isCapturingPhoto || photoStreamRef.current) {
      return;
    }

    try {
      setIsCapturingPhoto(true);

      // Wait a bit to ensure video element is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!photoVideoRef.current) {
        setIsCapturingPhoto(false);
        showAlert(t('prescriptionForm.alerts.cameraNotReady'), 'error');
        return;
      }

      // Request camera access only when user clicks the button
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      if (photoVideoRef.current) {
        photoVideoRef.current.srcObject = stream;
        photoStreamRef.current = stream;

        // Explicitly play the video (no autoplay attribute)
        try {
          await photoVideoRef.current.play();
        } catch (playError) {
          console.error('Error playing video:', playError);
          // Video might already be playing, continue anyway
        }
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setIsCapturingPhoto(false);

      let errorMessage = t('prescriptionForm.alerts.cameraFailed');
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = t('prescriptionForm.alerts.cameraDenied');
      } else if (error.name === 'NotFoundError') {
        errorMessage = t('prescriptionForm.alerts.cameraNotFound');
      } else if (error.name === 'NotReadableError') {
        errorMessage = t('prescriptionForm.alerts.cameraInUse');
      }

      showAlert(errorMessage, 'error');
    }
  };

  const stopPhotoCapture = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Stop all camera tracks
    if (photoStreamRef.current) {
      photoStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      photoStreamRef.current = null;
    }

    // Clear video element
    if (photoVideoRef.current) {
      photoVideoRef.current.pause();
      photoVideoRef.current.srcObject = null;
      photoVideoRef.current.load(); // Reset video element
    }

    setIsCapturingPhoto(false);
  };

  const capturePhoto = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (photoVideoRef.current && photoCanvasRef.current) {
      const video = photoVideoRef.current;
      const canvas = photoCanvasRef.current;

      // The camera stream can report 0×0 for a moment right after play() before
      // the first frame arrives. Capturing then yields an empty/broken image, so
      // wait for the video to be ready instead.
      if (!video.videoWidth || !video.videoHeight) {
        showAlert(t('prescriptionForm.alerts.cameraNotReady'), 'error');
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setPhotoPreview(dataUrl);
      stopPhotoCapture();
    }
  };

  const buyerRequiredFields = [
    { key: 'frameType', label: t('prescriptionForm.buyer.frameType') },
    { key: 'frameMaterial', label: t('prescriptionForm.buyer.frameMaterial') },
    { key: 'lensType', label: t('prescriptionForm.buyer.lensType') },
    { key: 'lensCoating', label: t('prescriptionForm.buyer.lensCoating') }
  ];

  const missingBuyerFields = selectedOption === 'buyer'
    ? buyerRequiredFields.filter((field) => !buyerChoice[field.key]?.trim())
    : [];

  const isBuyerDetailsComplete = selectedOption !== 'buyer' || missingBuyerFields.length === 0;
  const isOtherSelectionComplete = selectedOption !== 'other' || !!selectedOtherProduct;
  const isProfessionalSelectionComplete = selectedOption !== 'professional' || !!selectedRecommendation;
  const isOrderSelectionComplete = !!selectedOption && isBuyerDetailsComplete && isProfessionalSelectionComplete && isOtherSelectionComplete
    && bothLensesChosen;

  const hasBasePatientInfo = !!(
    formData.patientName &&
    formData.patientAge &&
    formData.prescriptionDate &&
    formData.prescribedBy &&
    formData.prescriptionType
  );

  const hasProgressiveRequirements = !!(
    formData.prescriptionType === 'progressive' &&
    ((formData.progressiveRightEyeFar.sphere && formData.progressiveLeftEyeFar.sphere) ||
      (formData.progressiveFarNotAvailable && formData.progressiveFarNotAvailableExplanation.trim()))
  );

  const hasSingleVisionRequirements = !!(
    formData.prescriptionType === 'single_vision' &&
    formData.singleVisionCorrectionType &&
    ((formData.singleVisionCorrectionType === 'far' && formData.rightEyeFar.sphere && formData.leftEyeFar.sphere) ||
      (formData.singleVisionCorrectionType === 'closeup' && formData.rightEyeCloseUp.sphere && formData.leftEyeCloseUp.sphere) ||
      (formData.singleVisionCorrectionType === 'both' && formData.rightEyeFar.sphere && formData.leftEyeFar.sphere && formData.rightEyeCloseUp.sphere && formData.leftEyeCloseUp.sphere))
  );

  const hasRequiredPrescriptionData = hasProgressiveRequirements || hasSingleVisionRequirements;
  const isOrderSectionReady = hasBasePatientInfo && hasRequiredPrescriptionData;

  const savePrescriptionToBackend = async ({ skipOrderValidation = false } = {}) => {
    try {
      if (!skipOrderValidation) {
        if (!selectedOption) {
          showAlert(t('prescriptionForm.alerts.selectOrderType'));
          return null;
        }
        if (!isProfessionalSelectionComplete) {
          showAlert(t('prescriptionForm.alerts.chooseRecommendation'));
          return null;
        }
        if (!bothLensesChosen) {
          showAlert(t('prescriptionForm.alerts.chooseBothLenses'));
          return null;
        }
        if (!isBuyerDetailsComplete) {
          showAlert(t('prescriptionForm.alerts.pleaseComplete', { fields: missingBuyerFields.map(field => field.label).join(', ') }));
          return null;
        }
      }

      // If the client refused progressive lenses, a reason is required.
      if (formData.progressiveRefused && !formData.progressiveRefusedReason.trim()) {
        showAlert(t('prescriptionForm.alerts.explainRefused'));
        return null;
      }

      // Validate Single Vision fields
      if (formData.prescriptionType === 'single_vision') {
        if (!formData.singleVisionCorrectionType) {
          showAlert(t('prescriptionForm.alerts.selectCorrection'));
          return null;
        }
        const needsFar = formData.singleVisionCorrectionType === 'far' || formData.singleVisionCorrectionType === 'both';
        const needsClose = formData.singleVisionCorrectionType === 'closeup' || formData.singleVisionCorrectionType === 'both';
        if (needsFar && (!formData.rightEyeFar.sphere || !formData.leftEyeFar.sphere)) {
          showAlert(t('prescriptionForm.alerts.enterFarBoth'));
          return null;
        }
        if (needsClose && (!formData.rightEyeCloseUp.sphere || !formData.leftEyeCloseUp.sphere)) {
          showAlert(t('prescriptionForm.alerts.enterCloseBoth'));
          return null;
        }
      }

      // Validate Progressive fields
      if (formData.prescriptionType === 'progressive') {
        const hasFarCorrection = formData.progressiveRightEyeFar.sphere && formData.progressiveLeftEyeFar.sphere;

        // Validate Far correction
        if (!formData.progressiveFarNotAvailable && !hasFarCorrection) {
          showAlert(t('prescriptionForm.alerts.provideFarOrExplain'));
          return null;
        }
        if (formData.progressiveFarNotAvailable && !formData.progressiveFarNotAvailableExplanation.trim()) {
          showAlert(t('prescriptionForm.alerts.explainFarNA'));
          return null;
        }
      }

      // Get visitor data from sessionStorage or existing visitorId
      let visitorId = sessionStorage.getItem('visitorId');

      // If no visitor ID exists, create visitor entry from sessionStorage data
      if (!visitorId) {
        const visitorDataStr = sessionStorage.getItem('visitorData');
        if (!visitorDataStr) {
          throw new Error('No visitor data found. Please start from visitor entry.');
        }

        const visitorData = JSON.parse(visitorDataStr);
        const timeTaken = parseInt(sessionStorage.getItem('visitorTimeTaken') || '0');

        // Get sales person from localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const salesPersonId = user.id || null;

        // Create visitor entry
        const visitorResponse = await axios.post('/api/visitors', {
          ...visitorData,
          timeTaken,
          salesPerson: salesPersonId
        }, {
          headers: getAuthHeaders()
        });

        visitorId = visitorResponse.data._id;
        sessionStorage.setItem('visitorId', visitorId);
      }

      // Sync Visitor Contact Info (Create "Client" link)
      if (visitorId && formData.patientName) {
        // The form captures first and last name separately, so use those verbatim.
        // Re-splitting the joined patientName guesses the boundary wrong whenever
        // either part is more than one word ("Mohamed El" + "Habib Khaoua").
        const nameParts = formData.patientName.trim().split(/\s+/);
        const firstName = (formData.patientFirstName || '').trim() || nameParts[0];
        const lastName  = (formData.patientLastName  || '').trim() || nameParts.slice(1).join(' ') || '';

        try {
          await axios.put(`/api/visitors/${visitorId}`, {
            firstName,
            lastName,
            phone: formData.phoneNumber,
            email: formData.email,
            address: formData.address,
            occupation: formData.occupation
          }, {
            headers: getAuthHeaders()
          });
        } catch (err) {
          console.error("Error syncing visitor contact info", err);
        }
      }

      const prescriptionData = {
        ...formData,
        visitor: visitorId,
        orderType: selectedOption || undefined,
        buyerChoice: selectedOption === 'buyer' ? buyerChoice : null,
        selectedRecommendation: selectedOption === 'professional' && selectedRecommendation ? {
          id: selectedRecommendation._id,
          name: selectedRecommendation.name,
          price: selectedRecommendation.price,
          estimatedDays: selectedRecommendation.estimatedDays ?? null
        } : selectedOption === 'other' && selectedOtherProduct ? {
          id: selectedOtherProduct.catalogId || selectedOtherProduct.name,
          name: selectedOtherProduct.name,
          price: selectedOtherProduct.price ?? null,
          estimatedDays: selectedOtherProduct.estimatedDays ?? null,
          // 'up' = premium upgrade, 'down' = cheaper alternative. Drives the
          // progressive tier in analytics (a plain recommendation is Fair).
          direction: otherDirection || null
        } : null
      };

      let response;
      try {
        response = await axios.post('/api/prescriptions', prescriptionData, { headers: getAuthHeaders() });
      } catch (error) {
        // Handle duplicate client error
        if (error.response?.status === 409 && error.response?.data?.isDuplicate) {
          const existingPrescriptions = error.response.data.existingPrescriptions;
          setExistingPrescriptionData(existingPrescriptions);
          setShowDuplicateModal(true);
          loadDuplicateHistory(existingPrescriptions?.[0]);
          return null; // Don't create prescription yet, wait for user decision
        } else {
          // Other errors - re-throw to be caught by outer catch
          throw error;
        }
      }

      // Store prescription ID in session storage
      if (response.data._id) {
        sessionStorage.setItem('prescriptionId', response.data._id);
        return response.data._id;
      }
      return null;
    } catch (error) {
      console.error('Error saving prescription:', error);
      showAlert(t('prescriptionForm.alerts.saveError', { msg: error.response?.data?.message || error.response?.data?.error || error.message }));
      return null;
    }
  };

  // Upload a captured photo (data URL) to an existing prescription record.
  // Retries a few times on transient network failures; throws on final failure.
  const uploadPhotoBlob = async (prescriptionId, dataUrl) => {
    const MAX_RETRIES = 3;
    let lastError = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const formDataToSend = new FormData();
        formDataToSend.append('prescriptionPhoto', blob, 'prescription.jpg');
        // Don't set Content-Type — let axios set the multipart boundary.
        await axios.put(`/api/prescriptions/${prescriptionId}/photo`, formDataToSend, {
          headers: getAuthHeaders(),
          timeout: 45000
        });
        return;
      } catch (error) {
        lastError = error;
        console.error(`Error uploading photo (Attempt ${attempt + 1}/${MAX_RETRIES}):`, error);
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    throw lastError;
  };

  const savePhoto = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!photoPreview) return;

    // The photo is captured at the very start of the prescription, before the
    // correction values are entered. If no prescription record exists yet, just
    // accept the photo locally now — it is uploaded during final submission once
    // the prescription has been created. Only upload immediately when we are
    // editing/resuming an existing prescription that already has an id.
    const existingId = sessionStorage.getItem('prescriptionId');
    if (!existingId) {
      setPrescriptionPhoto(photoPreview);
      setPhotoSaved(true);
      setPhotoUploaded(false);
      sessionStorage.setItem('hasPrescriptionPhoto', 'true');
      showAlert(t('prescriptionForm.alerts.photoSaved'), 'success');
      return;
    }

    try {
      setLoading(true);
      await uploadPhotoBlob(existingId, photoPreview);
      setPrescriptionPhoto(photoPreview);
      setPhotoSaved(true);
      setPhotoUploaded(true);
      sessionStorage.setItem('hasPrescriptionPhoto', 'true');
      showAlert(t('prescriptionForm.alerts.photoSaved'), 'success');
    } catch (error) {
      const userMsg = error.response?.data?.message || error.message || t('prescriptionForm.alerts.unknownNetwork');
      showAlert(t('prescriptionForm.alerts.photoCritical', { attempts: 3, msg: userMsg }), 'error');
      setPhotoSaved(false);
    } finally {
      setLoading(false);
    }
  };

  const retakePhoto = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPhotoPreview(null);
    setPhotoSaved(false);
    setPhotoUploaded(false);
    setPrescriptionPhoto(null);
    sessionStorage.removeItem('prescriptionPhotoState');
    sessionStorage.removeItem('hasPrescriptionPhoto');
  };

  // Draft order holding the chosen lens before the frame step. A "Both" sale
  // (two pairs) is still ONE order: it holds both lenses, each line naming its
  // pair (specifications.visionPair) so each is held in that pair's powers.
  const lensDraftLine = (pick, visionPair) => {
    const lens = lensOfPick(pick);
    if (!lens?.name) return null;
    const price = Number(lens.price) || 0;
    return {
      type: 'lens',
      name: lens.name,
      brand: 'Optiva',
      material: 'Polycarbonate',
      quantity: 1,
      unitPrice: price,
      totalPrice: price,
      specifications: {
        ...(visionPair ? { visionPair } : {}),
        sourceType: lens.inStock ? 'intern' : (lens.stockType === 'rx' ? 'rx' : 'extern'),
        // Keep the draft consistent with what Payment.js will write.
        lensDirection: pick.selectedOption === 'other' ? (pick.otherDirection || null) : null,
        tier: pick.selectedOption === 'other' && pick.otherDirection === 'up' ? 'premium'
          : pick.selectedOption === 'other' && pick.otherDirection === 'down' ? 'modest'
            : 'fair',
      },
    };
  };

  const reserveSelectedLensBeforeFrame = async (prescriptionId) => {
    const visitorId = sessionStorage.getItem('visitorId');
    const token = localStorage.getItem('token');
    const items = isBothPairs
      ? [lensDraftLine(farPick, 'far'), lensDraftLine(nearPick, 'near')]
      : [lensDraftLine(farPick, null)];
    if (!visitorId || !token || items.some((item) => !item)) return;

    const total = items.reduce((sum, item) => sum + item.totalPrice, 0);
    // The store picked at the start of the sale (admins); without it the
    // server files the order under the user's own store, which an admin lacks.
    let salesStoreId;
    try { salesStoreId = JSON.parse(sessionStorage.getItem('salesStore') || 'null')?.id; } catch (_) { salesStoreId = undefined; }
    const orderData = {
      visitor: visitorId,
      prescription: prescriptionId || null,
      ...(salesStoreId ? { store: salesStoreId } : {}),
      items,
      subtotal: total,
      tax: 0,
      discount: 0,
      total,
      payment: {
        method: 'cash',
        status: 'pending',
        amount: 0,
      },
      status: 'pending',
      reservationDraft: true,
    };

    const existingOrderId = sessionStorage.getItem('orderId');
    const requestConfig = {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 8000,
    };
    let response;
    try {
      response = existingOrderId
        ? await axios.put(`/api/orders/${existingOrderId}`, orderData, requestConfig)
        : await axios.post('/api/orders', orderData, requestConfig);
    } catch (err) {
      // The remembered order was cancelled since: this sale gets a new one.
      if (err.response?.data?.code !== 'ORDER_CANCELLED') throw err;
      ['orderId', 'orderNumber', 'orderData'].forEach((k) => sessionStorage.removeItem(k));
      response = await axios.post('/api/orders', orderData, requestConfig);
    }
    const order = response.data?.order || response.data;
    if (order?._id) {
      sessionStorage.setItem('orderId', order._id);
      sessionStorage.setItem('orderNumber', order.orderNumber);
      sessionStorage.setItem('orderData', JSON.stringify(order));
    }
  };

  // The far pick is the order's lens; with "Both" the close up pick is the
  // second pair's (selectedLensNear), sold as its own linked order.
  const selectedLensForOrder = lensOfPick(farPick);
  const nearLensForOrder = lensOfPick(nearPick);

  const switchLensPair = (next) => {
    if (next === lensPair) return;
    const saved = pairLenses[next];
    setLensPair(next);
    setSelectedOption(saved?.selectedOption || (formData.progressiveRefused ? 'other' : ''));
    setSelectedRecommendation(saved?.selectedRecommendation || null);
    setSelectedOtherProduct(saved?.selectedOtherProduct || null);
    setOtherDirection(saved ? saved.otherDirection : (formData.progressiveRefused ? 'down' : null));
  };

  useEffect(() => {
    if (!isBothPairs) {
      if (lensPair === 'near') switchLensPair('far');
      return;
    }
    setPairLenses((prev) => ({ ...prev, [lensPair]: { selectedOption, selectedRecommendation, selectedOtherProduct, otherDirection } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBothPairs, lensPair, selectedOption, selectedRecommendation, selectedOtherProduct, otherDirection]);

  const lensSessionEntry = (lens, pick) => (lens?.name ? JSON.stringify({
    id: lens._id || lens.catalogId || lens.name,
    // `name` stays the supplier/real name — orders and central-warehouse
    // stock matching key off it. `commercialName` is the selling name and
    // is the ONLY one shown to the client (confirmation page, receipt).
    name: lens.name,
    commercialName: lens.commercialName || '',
    price: lens.price ?? null,
    estimatedDays: lens.estimatedDays ?? null,
    orderType: pick.selectedOption,
    // Which slot this lens came from. Analytics turns this into the
    // progressive tier: recommendation → Fair, up → Premium, down → Modest.
    // Only meaningful for 'other'; the professional recommendation is Fair.
    direction: pick.selectedOption === 'other' ? (pick.otherDirection || null) : null,
  }) : null);

  const farLensEntry = lensSessionEntry(selectedLensForOrder, farPick || {});
  const nearLensEntry = lensSessionEntry(nearLensForOrder, nearPick || {});
  useEffect(() => {
    if (farLensEntry) sessionStorage.setItem('selectedLens', farLensEntry);
    else sessionStorage.removeItem('selectedLens');
    if (nearLensEntry) sessionStorage.setItem('selectedLensNear', nearLensEntry);
    else sessionStorage.removeItem('selectedLensNear');
  }, [farLensEntry, nearLensEntry]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // A progressive prescription must have an Add — block and warn if it's missing.
    if (formData.prescriptionType === 'progressive' && !formData.progressiveRefused) {
      const hasAdd = [
        formData.progressiveRightEyeFar?.add,
        formData.progressiveLeftEyeFar?.add,
        formData.progressiveRightEyeCloseUp?.add,
        formData.progressiveLeftEyeCloseUp?.add,
      ].map(v => parseFloat(v)).some(v => !Number.isNaN(v) && v > 0);
      if (!hasAdd) {
        showAlert("There's no Add — a progressive prescription needs an Add value before you can proceed.", 'error');
        return;
      }
    }

    setLoading(true);

    try {
      const prescriptionId = await savePrescriptionToBackend();
      if (prescriptionId) {
        // Upload the photo captured earlier, now that the prescription exists.
        if (photoPreview && !photoUploaded) {
          try {
            await uploadPhotoBlob(prescriptionId, photoPreview);
            setPhotoUploaded(true);
          } catch (photoError) {
            const userMsg = photoError.response?.data?.message || photoError.message || t('prescriptionForm.alerts.unknownNetwork');
            showAlert(t('prescriptionForm.alerts.photoCritical', { attempts: 3, msg: userMsg }), 'error');
            return; // Keep the user on the form so they can retry.
          }
        }
        try {
          await reserveSelectedLensBeforeFrame(prescriptionId);
        } catch (reserveError) {
          console.warn('Could not create draft lens order before frame selection:', reserveError.response?.data || reserveError.message);
        }
        // Navigate to frame selection
        navigate('/frame-selection');
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      // alert handled in helper
    } finally {
      setLoading(false);
    }
  };

  // Timers for press-and-hold on the correction -/+ buttons. One press at a
  // time, so a single ref covers every field.
  const rxHoldRef = useRef({ timeout: null, interval: null });
  useEffect(() => () => {
    clearTimeout(rxHoldRef.current.timeout);
    clearInterval(rxHoldRef.current.interval);
  }, []);

  // Renders an OD/OG correction table (rows stacked) with columns per measurement.
  // `eyes` = [{ label, key }]; `showAdd` adds an Add column; `readOnly` for auto-calculated values.
  const renderRxTable = ({ eyes, showAdd = false, readOnly = false }) => {
    const cols = showAdd ? 4 : 3;
    const rightKey = eyes[0] && eyes[0].key;
    const leftKey = eyes[1] && eyes[1].key;
    // Scrolling over a focused number input silently changes its value — blur to prevent it
    const preventWheel = (e) => e.currentTarget.blur();
    // Same steps and limits the blur validation enforces, so a stepped value is
    // always one the field would have accepted typed.
    const rxStep = (field) => (field === 'axis' ? 1 : 0.25);
    const rxBounds = (field) => (
      field === 'sphere' ? { min: -20, max: 20 }
        : field === 'cylinder' ? { min: -6, max: 6 }
        : field === 'axis' ? { min: 0, max: 180 }
        : { min: 0, max: 4 }
    );
    // An empty field steps from 0, so the first tap on "-" gives -0.25.
    const applyStep = (key, field, isFirstEye, direction) => {
      const step = rxStep(field);
      const { min, max } = rxBounds(field);
      setFormData(prev => {
        const current = parseFloat(prev[key][field]);
        const base = Number.isFinite(current) ? current : 0;
        const stepped = Math.round((base + direction * step) / step) * step;
        const next = Math.min(max, Math.max(min, stepped));
        const text = field === 'axis' ? String(next) : next.toFixed(2);
        const updated = { ...prev, [key]: { ...prev[key], [field]: text } };
        // Axis only means something with a cylinder, same rule as on blur.
        if (field === 'cylinder' && next === 0) updated[key].axis = '';
        // Add mirrors OD into OG, same as typing it does.
        if (field === 'add' && isFirstEye && leftKey) {
          updated[leftKey] = { ...prev[leftKey], add: text };
        }
        return updated;
      });
    };
    const stopHold = () => {
      clearTimeout(rxHoldRef.current.timeout);
      clearInterval(rxHoldRef.current.interval);
      rxHoldRef.current = { timeout: null, interval: null };
    };
    // Holding runs the step repeatedly — reaching -3.00 by tapping 12 times is
    // not a realistic way to enter a prescription on a tablet.
    const startHold = (e, key, field, isFirstEye, direction) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault(); // keep focus, and the on-screen keyboard, where it is
      stopHold();
      applyStep(key, field, isFirstEye, direction);
      rxHoldRef.current.timeout = setTimeout(() => {
        rxHoldRef.current.interval = setInterval(
          () => applyStep(key, field, isFirstEye, direction), 80
        );
      }, 500);
    };
    const renderCell = (key, field, isFirstEye) => {
      const isAxis = field === 'axis';
      const isAdd = field === 'add';
      let editProps;
      if (readOnly) {
        editProps = { readOnly: true };
      } else if (isAdd && isFirstEye && leftKey) {
        // Entering Add on OD mirrors it into OG (OG stays editable if it differs).
        // Add is always positive — block the minus sign, like axis.
        editProps = {
          onChange: (e) => {
            const v = e.target.value;
            setFormData(prev => ({
              ...prev,
              [rightKey]: { ...prev[rightKey], add: v },
              [leftKey]: { ...prev[leftKey], add: v }
            }));
          },
          onBlur: handleBlur,
          onKeyDown: blockMinusKey,
          min: '0'
        };
      } else {
        editProps = {
          onChange: handleChange,
          onBlur: handleBlur,
          ...(isAxis ? { onKeyDown: blockMinusKey, min: '0', max: '180', disabled: !formData[key].cylinder }
            : isAdd ? { onKeyDown: blockMinusKey, min: '0' }
            : {})
        };
      }
      const stepDisabled = readOnly || (isAxis && !formData[key].cylinder);
      const stepButton = (direction) => (
        <RxStepButton
          type="button"
          tabIndex={-1}
          disabled={stepDisabled}
          aria-label={t(direction < 0 ? 'rx.decrease' : 'rx.increase', { field: t(`rx.${field}`) })}
          onPointerDown={(e) => startHold(e, key, field, isFirstEye, direction)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          onContextMenu={(e) => e.preventDefault()}
        >
          {direction < 0 ? '−' : '+'}
        </RxStepButton>
      );
      return (
        <div>
          <RxFieldRow>
            {stepButton(-1)}
            <Input
              type="number"
              inputMode="decimal"
              {...(isAxis ? {} : { step: '0.25' })}
              name={`${key}.${field}`}
              value={formData[key][field]}
              placeholder={isAxis ? '0' : '0.00'}
              onWheel={preventWheel}
              {...editProps}
            />
            {stepButton(1)}
          </RxFieldRow>
          {renderTranspositionValue(formData[key], field)}
        </div>
      );
    };
    return (
      <RxTableScroll>
        <RxTable $cols={cols}>
          <RxHeadCell />
          <RxHeadCell>{t('rx.sphere')}</RxHeadCell>
          <RxHeadCell>{t('rx.cylinder')}</RxHeadCell>
          <RxHeadCell>{t('rx.axis')}</RxHeadCell>
          {showAdd && <RxHeadCell>{t('rx.add')}</RxHeadCell>}
          {eyes.map((eye, idx) => (
            <React.Fragment key={eye.key}>
              <RxRowLabel>{eye.label}</RxRowLabel>
              {renderCell(eye.key, 'sphere', idx === 0)}
              {renderCell(eye.key, 'cylinder', idx === 0)}
              {renderCell(eye.key, 'axis', idx === 0)}
              {showAdd && renderCell(eye.key, 'add', idx === 0)}
            </React.Fragment>
          ))}
        </RxTable>
      </RxTableScroll>
    );
  };

  const progressiveAge = parseInt(formData.patientAge, 10);
  const showProgressiveBanner = !isNaN(progressiveAge) && progressiveAge >= 43 && !formData.progressiveRefused;

  return (
    <Container>
      {stickySlot && ReactDOM.createPortal(
        <PinnedStrip $alert={showProgressiveBanner}>
          {showProgressiveBanner && (
            <ProgressiveBanner role="alert">
              <span>{t('prescriptionForm2.progressiveClientIs43Or')}</span>
            </ProgressiveBanner>
          )}
          <CatalogPinButton type="button" onClick={() => openCatalogBrowser(true)}>
            <Eye size={15} /> {t('prescriptionForm2.lensCatalog')}
          </CatalogPinButton>
        </PinnedStrip>,
        stickySlot,
      )}
      {showCatalogBrowser && createPortal((
        <div onClick={() => setShowCatalogBrowser(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '3rem 1rem', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 840, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e293b' }}>{t('prescriptionForm2.lensCatalogPricing')}</h3>
              <button type="button" onClick={() => setShowCatalogBrowser(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={20} color="#64748b" /></button>
            </div>
            <div style={{ padding: '0.85rem 1.25rem', display: 'flex', gap: '0.6rem', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
              <input type="text" value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} placeholder={t('prescriptionForm2.searchCommercialOrRealName')} style={{ flex: 1, minWidth: 200, padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem' }} />
              <select value={catalogDispoFilter} onChange={(e) => setCatalogDispoFilter(e.target.value)} style={{ padding: '0.55rem 0.6rem', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.85rem' }}>
                <option value="all">{t('prescriptionForm2.allDispo')}</option>
                <option value="int">{t('prescriptionForm2.stockInt')}</option>
                <option value="ext">{t('prescriptionForm2.stockExt')}</option>
                <option value="rx">{t('prescriptionForm2.rx2')}</option>
              </select>
            </div>
            <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
              {fullCatalogLoading ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>{t('prescriptionForm2.loadingCatalog')}</p>
              ) : (() => {
                const q = catalogSearch.trim().toLowerCase();
                const rows = (fullCatalog || []).filter(r => {
                  const dispo = String(r.availability || '').toLowerCase();
                  if (catalogDispoFilter === 'int' && !dispo.includes('int')) return false;
                  if (catalogDispoFilter === 'ext' && !dispo.includes('ext')) return false;
                  if (catalogDispoFilter === 'rx' && !dispo.includes('rx')) return false;
                  if (!q) return true;
                  return [r.commercialName, r.name, r.group, r.indexValue].some(x => String(x || '').toLowerCase().includes(q));
                });
                if (rows.length === 0) return <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>{t('prescriptionForm2.noLensesMatch')}</p>;
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#475569', position: 'sticky', top: 0, background: '#f8fafc' }}>{t('prescriptionForm2.nomCommercial')}</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#475569', position: 'sticky', top: 0, background: '#f8fafc' }}>{t('prescriptionForm2.nomREl')}</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: '#475569', position: 'sticky', top: 0, background: '#f8fafc' }}>{t('prescriptionForm2.groupe')}</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: '#475569', position: 'sticky', top: 0, background: '#f8fafc' }}>{t('prescriptionForm2.indice')}</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', color: '#475569', position: 'sticky', top: 0, background: '#f8fafc' }}>{t('prescriptionForm2.dispo')}</th>
                        <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#475569', position: 'sticky', top: 0, background: '#f8fafc' }}>{t('prescriptionForm2.prix')}</th>
                        <th style={{ position: 'sticky', top: 0, background: '#f8fafc' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r._id || i} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: '#0f172a' }}>{r.commercialName || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#475569' }}>{r.name}</td>
                          <td style={{ padding: '0.5rem 0.5rem', color: '#475569' }}>{r.group || '—'}</td>
                          <td style={{ padding: '0.5rem 0.5rem', color: '#475569' }}>{r.indexValue || '—'}</td>
                          <td style={{ padding: '0.5rem 0.5rem', color: '#475569' }}>{r.availability || '—'}</td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{r.price != null ? r.price.toLocaleString() : '—'}</td>
                          <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right' }}>
                            {!catalogViewOnly && (<button type="button" onClick={() => chooseCatalogLens(r)} style={{ padding: '0.35rem 0.7rem', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>{t('prescriptionForm2.select')}</button>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      ), document.body)}
      <Form onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>{t('prescriptionForm.patientInfo')}</SectionTitle>
          <PatientIdentityRow>
            <FormGroup style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Label htmlFor="patientFirstName" style={{ marginBottom: 0 }}>{t('prescriptionForm.firstName')} *</Label>
                {clientNameMatches.length > 0 && (
                  <ExistingClientBadge
                    type="button"
                    title={t('prescriptionForm2.existingClientBadgeTitle')}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowClientNameMatches(prev => !prev);
                    }}
                  >
                    {t('prescriptionForm2.existingClientBadge')}
                  </ExistingClientBadge>
                )}
              </div>
              <Input
                type="text"
                id="patientFirstName"
                value={formData.patientFirstName}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^a-zA-Z\s'-]/g, '');
                  value = value.replace(/\b\w/g, c => c.toUpperCase());
                  const full = `${value} ${formData.patientLastName}`.trim();
                  suppressClientNameSearchRef.current = false;
                  setFormData(prev => ({ ...prev, patientFirstName: value, patientName: full }));
                }}
                onBlur={() => {
                  setTimeout(() => setShowClientNameMatches(false), 160);
                }}
                placeholder={t('prescriptionForm.firstNamePh')}
                required
              />
              {showClientNameMatches && clientNameMatches.length > 0 && (
                <div style={{
                  position: 'absolute',
                  zIndex: 50,
                  left: 0,
                  right: 0,
                  top: '100%',
                  marginTop: '0.35rem',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxShadow: '0 18px 35px rgba(15, 23, 42, 0.16)',
                  overflow: 'hidden',
                  maxHeight: '16rem',
                  overflowY: 'auto'
                }}>
                  {clientNameMatches.map((client) => {
                      const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed client';
                      return (
                        <button
                          key={client.clientKey || client.visitorId || client._id || fullName}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectExistingClientMatch(client);
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'white',
                            padding: '0.8rem 0.95rem',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            fontFamily: 'inherit'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                            <strong style={{ color: '#1e293b' }}>{fullName}</strong>
                            <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                              {t('prescriptionForm2.dob')} {client.birthday ? new Date(client.birthday).toLocaleDateString() : t('prescriptionForm2.nA')}
                            </span>
                          </div>
                          <div style={{ marginTop: '0.25rem', color: '#64748b', fontSize: '0.8rem' }}>
                            {client.phone || 'No phone'} {t('prescriptionForm2.lastVisit')} {client.lastVisit ? new Date(client.lastVisit).toLocaleDateString() : t('prescriptionForm2.nA')}
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label htmlFor="patientLastName">{t('prescriptionForm.lastName')} *</Label>
              <Input
                type="text"
                id="patientLastName"
                value={formData.patientLastName}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^a-zA-Z\s'-]/g, '');
                  value = value.replace(/\b\w/g, c => c.toUpperCase());
                  const full = `${formData.patientFirstName} ${value}`.trim();
                  setFormData(prev => ({ ...prev, patientLastName: value, patientName: full }));
                }}
                placeholder={t('prescriptionForm.lastNamePh')}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="patientBirthday">{t('prescriptionForm.dob')}</Label>
              <DateInputDDMMYYYY
                id="patientBirthday"
                hidePicker
                value={formData.patientBirthday}
                onChange={(iso) => handleChange({ target: { name: 'patientBirthday', value: iso } })}
                onCommit={(iso) => handleBlur({ target: { name: 'patientBirthday', value: iso } })}
                max={new Date().toISOString().split('T')[0]}
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split('T')[0]}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="patientAge">{t('prescriptionForm2.age')}{formData.patientBirthday ? '' : ' *'}</Label>
              {/* type="number" still lets you type e, E, +, - and . (they are
                  legal in exponent notation), and min/max are only checked on
                  submit — so "1e5" and "-5" both got through as an age. Text
                  input with digits stripped on the way in is the only way to
                  actually get digits. inputMode keeps the numeric keypad on a
                  tablet. */}
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={3}
                id="patientAge"
                name="patientAge"
                value={formData.patientAge || ''}
                readOnly={!!formData.patientBirthday}
                onChange={(e) => {
                  if (formData.patientBirthday) return;
                  const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                  if (digits === '') {
                    setFormData(prev => ({ ...prev, patientAge: '' }));
                    return;
                  }
                  // Clamp as they type rather than rejecting on submit — an age
                  // of 999 is a typo, not something to argue about later.
                  const clamped = Math.min(120, parseInt(digits, 10));
                  setFormData(prev => ({ ...prev, patientAge: String(clamped) }));
                }}
                placeholder={formData.patientBirthday ? '' : 'Enter age'}
                style={{
                  backgroundColor: formData.patientBirthday ? '#f8fafc' : '#fff',
                  color: formData.patientAge ? '#1e293b' : '#64748b',
                  cursor: formData.patientBirthday ? 'not-allowed' : 'text'
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="address">{t('prescriptionForm.address')} *</Label>
              <Input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={(e) => {
                  // Auto-capitalize first letter of each word
                  let value = e.target.value.replace(/\b\w/g, (char) => char.toUpperCase());
                  setFormData(prev => ({ ...prev, address: value }));
                }}
                onBlur={handleBlur}
                placeholder={t('prescriptionForm.addressPh')}
                required
              />
            </FormGroup>
          </PatientIdentityRow>
          <PatientContactRow>
            <FormGroup>
              <Label htmlFor="phoneNumber">{t('prescriptionForm.phoneNumber')} *</Label>
              <Input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => {
                  // Only allow numbers
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setFormData(prev => ({
                    ...prev,
                    phoneNumber: value
                  }));
                }}
                placeholder={t('prescriptionForm.phonePh')}
                required
                pattern="^[0-9]+$"
                title={t('prescriptionForm2.onlyNumbersAreAllowed')}
                maxLength={15}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="occupation">{t('prescriptionForm.occupation')}</Label>
              {/* Click the chevron (or focus) to open the list, type to filter,
                  and the always-visible "+" adds a new one to the shared list. */}
              <div style={{ position: 'relative' }}>
                <DoctorSelectWrapper>
                  <Input
                    type="text"
                    id="occupation"
                    name="occupation"
                    value={formData.occupation}
                    onChange={(e) => { setFormData(prev => ({ ...prev, occupation: e.target.value })); setOccOpen(true); }}
                    onFocus={() => setOccOpen(true)}
                    onBlur={() => setTimeout(() => setOccOpen(false), 150)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOccupation(); setOccOpen(false); } if (e.key === 'Escape') setOccOpen(false); }}
                    placeholder={t('prescriptionForm.occupationPh')}
                    autoComplete="off"
                    style={{ flex: 1, paddingRight: '2.25rem' }}
                  />
                  <div
                    onMouseDown={(e) => { e.preventDefault(); setOccOpen((o) => !o); }}
                    style={{ position: 'absolute', right: 'calc(44px + 0.5rem + 1rem)', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', cursor: 'pointer', display: 'flex' }}
                    title={t('prescriptionForm2.showOccupations')}
                  >
                    <ChevronDown size={18} />
                  </div>
                  <AddDoctorBtn type="button" title={t('prescriptionForm2.addOccupation')} onClick={addOccupation}>
                    <Plus size={18} />
                  </AddDoctorBtn>
                </DoctorSelectWrapper>
                {occOpen && (() => {
                  const q = String(formData.occupation || '').trim().toLowerCase();
                  const list = q ? occupations.filter((o) => (o.name || '').toLowerCase().includes(q)) : occupations;
                  if (list.length === 0) return null;
                  return (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: '52px', zIndex: 30, background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, marginTop: 4, maxHeight: 220, overflowY: 'auto', boxShadow: '0 10px 28px rgba(0,0,0,0.14)' }}>
                      {list.map((o) => (
                        <div
                          key={o._id}
                          onMouseDown={(e) => { e.preventDefault(); setFormData(prev => ({ ...prev, occupation: o.name })); setOccOpen(false); }}
                          style={{ padding: '0.55rem 0.8rem', cursor: 'pointer', fontSize: '0.9rem', color: '#1f2937' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                        >
                          {o.name}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {occupationMsg && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <CheckCircle size={14} /> {occupationMsg}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label htmlFor="prescriptionDate">{t('prescriptionForm.prescriptionDate')}</Label>
              <DateInputDDMMYYYY
                id="prescriptionDate"
                hidePicker
                value={formData.prescriptionDate}
                onChange={(iso) => handleChange({ target: { name: 'prescriptionDate', value: iso } })}
                onCommit={(iso) => handleBlur({ target: { name: 'prescriptionDate', value: iso } })}
                max={new Date().toISOString().split('T')[0]}
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 2)).toISOString().split('T')[0]}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="prescribedBy">{t('prescriptionForm.prescribedBy')} *</Label>
              <DoctorSelectWrapper>
                <DoctorSelectField
                  id="prescribedBy"
                  name="prescribedBy"
                  value={formData.prescribedBy}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      prescribedBy: e.target.value
                    }));
                  }}
                  required
                >
                  <option value="">{t('prescriptionForm.selectDoctor')}</option>
                  {doctors.map(doc => (
                    <option key={doc._id} value={doc.name}>{doc.name}</option>
                  ))}
                </DoctorSelectField>
                <DoctorSelectIcon>
                  <ChevronDown size={16} />
                </DoctorSelectIcon>
                <AddDoctorBtn
                  type="button"
                  title={t('prescriptionForm2.addNewDoctor')}
                  onClick={() => setShowAddDoctorModal(true)}
                >
                  <UserPlus size={18} />
                </AddDoctorBtn>
              </DoctorSelectWrapper>
            </FormGroup>

            {showAddDoctorModal && (
              <InlineDoctorModal onClick={() => { setShowAddDoctorModal(false); setAddDoctorSuccess(''); }}>
                <InlineDoctorContent onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <InlineDoctorTitle>
                      <UserPlus size={22} color="#06b6d4" />
                      {t('prescriptionForm2.addNewDoctor2')}
                    </InlineDoctorTitle>
                    <button
                      type="button"
                      onClick={() => { setShowAddDoctorModal(false); setAddDoctorSuccess(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <X size={20} color="#64748b" />
                    </button>
                  </div>

                  {addDoctorSuccess && (
                    <InlineDoctorSuccess>
                      <CheckCircle size={16} />
                      {addDoctorSuccess}
                    </InlineDoctorSuccess>
                  )}

                  <InlineDoctorFormGroup>
                    <InlineDoctorLabel>{t('prescriptionForm2.name')}</InlineDoctorLabel>
                    <InlineDoctorInput
                      type="text"
                      placeholder={t('prescriptionForm.doctorPh')}
                      value={newDoctorForm.name}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^a-zA-Z\s'.-]/g, '');
                        // Auto-capitalize first letter of each word
                        value = value.replace(/\b\w/g, (char) => char.toUpperCase());
                        setNewDoctorForm(prev => ({ ...prev, name: value }));
                      }}
                    />
                  </InlineDoctorFormGroup>

                  <InlineDoctorFormGroup>
                    <InlineDoctorLabel style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Phone size={13} /> {t('prescriptionForm2.phoneNumber')}
                    </InlineDoctorLabel>
                    <InlineDoctorInput
                      type="tel"
                      placeholder={t('prescriptionForm2.optional')}
                      value={newDoctorForm.phoneNumber}
                      onChange={(e) => setNewDoctorForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    />
                  </InlineDoctorFormGroup>

                  <InlineDoctorFormGroup>
                    <InlineDoctorLabel style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <MapPin size={13} /> {t('prescriptionForm2.address')}
                    </InlineDoctorLabel>
                    <InlineDoctorInput
                      type="text"
                      placeholder={t('prescriptionForm2.optional')}
                      value={newDoctorForm.address}
                      onChange={(e) => {
                        // Auto-capitalize first letter of each word
                        let value = e.target.value.replace(/\b\w/g, (char) => char.toUpperCase());
                        setNewDoctorForm(prev => ({ ...prev, address: value }));
                      }}
                    />
                  </InlineDoctorFormGroup>

                  <InlineDoctorFormGroup>
                    <InlineDoctorLabel style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <MapPin size={13} /> {t('prescriptionForm2.city')}
                    </InlineDoctorLabel>
                    <InlineDoctorInput
                      type="text"
                      placeholder={t('prescriptionForm2.optional')}
                      value={newDoctorForm.city}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\b\w/g, (char) => char.toUpperCase());
                        setNewDoctorForm(prev => ({ ...prev, city: value }));
                      }}
                    />
                  </InlineDoctorFormGroup>

                  <InlineDoctorFormGroup>
                    <InlineDoctorLabel style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <MapPin size={13} /> {t('prescriptionForm2.country')}
                    </InlineDoctorLabel>
                    <InlineDoctorInput
                      type="text"
                      placeholder={t('prescriptionForm2.optional')}
                      value={newDoctorForm.country}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\b\w/g, (char) => char.toUpperCase());
                        setNewDoctorForm(prev => ({ ...prev, country: value }));
                      }}
                    />
                  </InlineDoctorFormGroup>

                  <InlineDoctorActions>
                    <InlineDoctorBtn
                      type="button"
                      className="cancel"
                      onClick={() => { setShowAddDoctorModal(false); setAddDoctorSuccess(''); }}
                    >
                      {t('prescriptionForm2.cancel')}
                    </InlineDoctorBtn>
                    <InlineDoctorBtn
                      type="button"
                      className="save"
                      onClick={handleAddDoctorInline}
                      disabled={!newDoctorForm.name.trim() || savingDoctor}
                    >
                      {savingDoctor ? t('prescriptionForm2.saving') : t('prescriptionForm2.saveDoctor')}
                    </InlineDoctorBtn>
                  </InlineDoctorActions>
                </InlineDoctorContent>
              </InlineDoctorModal>
            )}
          </PatientContactRow>
        </FormSection>

        {previousPrescription && (
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <SectionTitle style={{ margin: 0 }}>{t('prescriptionForm2.previousPrescription')}</SectionTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                {rxReused && (
                  <span style={{ color: '#059669', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                    <CheckCircle size={14} /> {t('prescriptionForm2.copiedToThisVisit')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={reusePreviousRx}
                  style={{ padding: '0.5rem 0.9rem', background: 'linear-gradient(135deg,#06b6d4,#0891b2)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  <Save size={15} /> {t('prescriptionForm2.reuseThisRx')}
                </button>
              </div>
            </div>
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '14px',
              padding: '1rem',
              marginBottom: '0.5rem'
            }}>
              {/* Client and prescription details read as one line; the dot
                  separators collapse onto a second line only if space runs out. */}
              <PrevRxMeta>
                <strong>{previousPrescription.patientName || formData.patientName || 'Previous client'}</strong>
                <span>{formatPreviousRxValue(previousPrescription.phoneNumber || formData.phoneNumber)}</span>
                <span>{previousPrescription.prescriptionType === 'progressive' ? t('prescriptionForm2.progressive') : t('prescriptionForm2.singleVision')}</span>
                <span>{previousPrescription.prescriptionDate ? new Date(previousPrescription.prescriptionDate).toLocaleDateString() : '—'}</span>
                {previousPrescription.prescribedBy && (
                  <span>{t('prescriptionForm2.dr')} {String(previousPrescription.prescribedBy).replace(/^Dr\.?\s*/i, '')}</span>
                )}
              </PrevRxMeta>

              {/* Correction tables sit beside the photo rather than stacked. */}
              <PrevRxBody>
                <PrevRxTables>
                  {getPreviousPrescriptionBlocks(previousPrescription).map((block, blockIndex) => {
                    const od = block.od || {};
                    const os = block.os || {};
                    return (
                      <div key={`${block.title}-${blockIndex}`} style={{ marginTop: blockIndex ? '0.75rem' : 0, overflowX: 'auto' }}>
                        <h4>{block.title}</h4>
                        <table>
                          <thead>
                            <tr>
                              <th>{t('prescriptionForm2.eye')}</th>
                              <th>{t('prescriptionForm2.sph')}</th>
                              <th>{t('prescriptionForm2.cyl')}</th>
                              <th>{t('prescriptionForm2.axis')}</th>
                              {block.showAdd && <th>{t('prescriptionForm2.add')}</th>}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="eye">{t('prescriptionForm2.od')}</td>
                              <td>{formatPreviousRxValue(od.sphere)}</td>
                              <td>{formatPreviousRxValue(od.cylinder)}</td>
                              <td>{formatPreviousRxValue(od.axis)}</td>
                              {block.showAdd && <td>{formatPreviousRxValue(od.add)}</td>}
                            </tr>
                            <tr>
                              <td className="eye">{t('prescriptionForm2.og')}</td>
                              <td>{formatPreviousRxValue(os.sphere)}</td>
                              <td>{formatPreviousRxValue(os.cylinder)}</td>
                              <td>{formatPreviousRxValue(os.axis)}</td>
                              {block.showAdd && <td>{formatPreviousRxValue(os.add)}</td>}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </PrevRxTables>

                {previousPrescription.prescriptionPhoto && (
                  <PrevRxPhoto>
                    <img
                      src={previousPrescription.prescriptionPhoto}
                      alt={t('prescriptionForm2.previousPrescription2')}
                    />
                    <HistoryPhotoButton
                      type="button"
                      onClick={() => setHistoryPrescriptionPhoto({
                        src: previousPrescription.prescriptionPhoto,
                        title: `Previous prescription photo - ${previousPrescription.prescriptionDate ? new Date(previousPrescription.prescriptionDate).toLocaleDateString() : 'N/A'}`
                      })}
                    >
                      <Camera size={14} />
                      {t('prescriptionForm2.viewPhoto')}
                    </HistoryPhotoButton>
                  </PrevRxPhoto>
                )}
              </PrevRxBody>
            </div>
          </FormSection>
        )}

        <FormSection>
          <SectionTitle>{t('prescriptionForm.eyeMeasurements')}</SectionTitle>
          {/* The age notice and the "Special case" toggle share a line: the
              toggle is the exception to the rule the notice just stated. */}
          <RxNoticeRow>
          {(() => {
            const ageNum = parseInt(formData.patientAge, 10);
            const hasValidAge = !isNaN(ageNum) && ageNum > 0;
            if (!hasValidAge) {
              return (
                <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic' }}>
                  {t('prescriptionForm2.enterDateOfBirthAbove')}
                </p>
              );
            }
            // 43+ and not refused: the notice is pinned at the top of the page
            // (see the portal below), so nothing more is needed here.
            if (ageNum >= 43 && !formData.progressiveRefused) return null;
            if (ageNum >= 43 && formData.progressiveRefused) {
              return (
                <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#b45309', fontStyle: 'italic' }}>
                  {t('prescriptionForm2.clientRefusedProgressiveSwitchedTo')}
                </p>
              );
            }
            return (
              <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#0369a1', fontStyle: 'italic' }}>
                {t('prescriptionForm2.singleVisionClientIsUnder')}
              </p>
            );
          })()}

          </RxNoticeRow>

          <RxLayout>
            <RxMainCol>
              {/* Single Vision: far-only by default. "Special case" (under 43) or a
                  43+ progressive refusal unlocks the Close Up / Both chooser.
                  The toggle heads the correction column because it governs which
                  correction blocks appear below it. */}
              {formData.prescriptionType === 'single_vision' && (
                <FormGroup style={{ marginTop: 0 }}>
                  {!formData.progressiveRefused && (
                    <SpecialCaseToggle>
                      <input
                        type="checkbox"
                        checked={formData.specialCase}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            specialCase: checked,
                            singleVisionCorrectionType: checked ? (prev.singleVisionCorrectionType || 'far') : 'far'
                          }));
                        }}
                      />
                      <span>{t('prescriptionForm.specialCase')}</span>
                    </SpecialCaseToggle>
                  )}

                  {(formData.specialCase || formData.progressiveRefused) && (
                    <>
                      <Label>{t('prescriptionForm.correctionQuestion')} *</Label>
                      <CorrectionTypeContainer>
                        <CorrectionTypeBox
                          selected={formData.singleVisionCorrectionType === 'far'}
                          onClick={() => setFormData(prev => ({ ...prev, singleVisionCorrectionType: 'far' }))}
                        >
                          {t('prescriptionForm2.far')}
                        </CorrectionTypeBox>
                        <CorrectionTypeBox
                          selected={formData.singleVisionCorrectionType === 'closeup'}
                          onClick={() => setFormData(prev => ({ ...prev, singleVisionCorrectionType: 'closeup' }))}
                        >
                          {t('prescriptionForm2.closeUp')}
                        </CorrectionTypeBox>
                        <CorrectionTypeBox
                          selected={formData.singleVisionCorrectionType === 'both'}
                          onClick={() => setFormData(prev => ({ ...prev, singleVisionCorrectionType: 'both' }))}
                        >
                          {t('prescriptionForm2.both')}
                        </CorrectionTypeBox>
                      </CorrectionTypeContainer>
                    </>
                  )}
                </FormGroup>
              )}

              {/* Progressive - Far Correction */}
              {formData.prescriptionType === 'progressive' && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem', gap: '0.5rem' }}>
                    <RxBlockTitle>{t('prescriptionForm2.farVisionCorrection')}</RxBlockTitle>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.7rem',
                      borderRadius: '9999px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                      background: formData.trialLens ? '#eef2ff' : '#f8fafc',
                      border: `1px solid ${formData.trialLens ? '#c7d2fe' : '#e2e8f0'}`,
                      color: formData.trialLens ? '#3730a3' : '#475569',
                    }}>
                      <input
                        type="checkbox"
                        checked={!!formData.trialLens}
                        onChange={(e) => setFormData((prev) => ({ ...prev, trialLens: e.target.checked }))}
                        style={{ margin: 0 }}
                      />
                      {t('prescriptionForm2.trialLens')}
                      <span style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 500 }}>{t('prescriptionForm2.trialLensDays')}</span>
                    </label>
                    {(progFarGroups.rightEye || progFarGroups.leftEye) && (
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        {progFarGroups.rightEye && <><span style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('prescriptionForm2.od2')}</span><GroupBadge group={progFarGroups.rightEye} /></>}
                        {progFarGroups.leftEye && <><span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '0.35rem' }}>{t('prescriptionForm2.os')}</span><GroupBadge group={progFarGroups.leftEye} /></>}
                      </div>
                    )}
                  </div>
                  {!formData.progressiveFarNotAvailable && renderRxTable({
                    eyes: [
                      { label: 'OD', key: 'progressiveRightEyeFar' },
                      { label: 'OG', key: 'progressiveLeftEyeFar' }
                    ],
                    showAdd: true
                  })}
                </div>
              )}

              {/* Single Vision - Far Correction */}
              {formData.prescriptionType === 'single_vision' &&
                (formData.singleVisionCorrectionType === 'far' || formData.singleVisionCorrectionType === 'both') && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem', gap: '0.5rem' }}>
                      <RxBlockTitle>{t('prescriptionForm.farVision')}</RxBlockTitle>
                      {(svFarGroups.rightEye || svFarGroups.leftEye) && (
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          {svFarGroups.rightEye && <><span style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('prescriptionForm2.od2')}</span><GroupBadge group={svFarGroups.rightEye} /></>}
                          {svFarGroups.leftEye && <><span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '0.35rem' }}>{t('prescriptionForm2.os')}</span><GroupBadge group={svFarGroups.leftEye} /></>}
                        </div>
                      )}
                    </div>
                    {renderRxTable({
                      eyes: [
                        { label: 'OD', key: 'rightEyeFar' },
                        { label: 'OG', key: 'leftEyeFar' }
                      ],
                      showAdd: parseInt(formData.patientAge, 10) >= 43
                    })}
                  </div>
                )}

              {/* Single Vision - Close Up Correction */}
              {formData.prescriptionType === 'single_vision' &&
                (formData.singleVisionCorrectionType === 'closeup' || formData.singleVisionCorrectionType === 'both') && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem', gap: '0.5rem' }}>
                      <RxBlockTitle>{t('prescriptionForm.closeUpVision')}</RxBlockTitle>
                      {(svCloseUpGroups.rightEye || svCloseUpGroups.leftEye) && (
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          {svCloseUpGroups.rightEye && <><span style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('prescriptionForm2.od2')}</span><GroupBadge group={svCloseUpGroups.rightEye} /></>}
                          {svCloseUpGroups.leftEye && <><span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '0.35rem' }}>{t('prescriptionForm2.os')}</span><GroupBadge group={svCloseUpGroups.leftEye} /></>}
                        </div>
                      )}
                    </div>
                    {renderRxTable({
                      eyes: [
                        { label: 'OD', key: 'rightEyeCloseUp' },
                        { label: 'OG', key: 'leftEyeCloseUp' }
                      ],
                      showAdd: parseInt(formData.patientAge, 10) >= 43
                    })}
                  </div>
                )}
            </RxMainCol>

            <RxPhotoCol>
              {/* Prescription photo — captured at the very start of the prescription,
                  before the correction values are entered. */}
              {formData.prescriptionType && (
                <div>
                  <PhotoHeading>
                    <Camera size={16} />
                    {t('prescriptionForm.prescriptionPhoto')}
                  </PhotoHeading>
                  <PhotoSection>
                    {!photoSaved ? (
                      <>
                        {!isCapturingPhoto && !photoPreview ? (
                          <>
                            <PhotoDropZone onClick={startPhotoCapture} role="button" tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startPhotoCapture(); } }}>
                              <span className="icon"><Camera size={20} /></span>
                              <p>{t('prescriptionForm2.takeAPhotoOfThe')}</p>
                            </PhotoDropZone>
                            <PhotoButtonGroup>
                              <PhotoButton type="button" primary onClick={startPhotoCapture}>
                                <Camera size={18} />
                                {t('prescriptionForm2.startCamera')}
                              </PhotoButton>
                            </PhotoButtonGroup>
                          </>
                        ) : isCapturingPhoto && !photoPreview ? (
                          <>
                            <CameraContainer>
                              <Video ref={photoVideoRef} show={true} playsInline />
                              <Canvas ref={photoCanvasRef} />
                            </CameraContainer>
                            <PhotoButtonGroup>
                              <PhotoButton type="button" onClick={stopPhotoCapture}>
                                <X size={18} />
                                {t('prescriptionForm2.cancel')}
                              </PhotoButton>
                              <PhotoButton type="button" primary onClick={capturePhoto}>
                                <Camera size={18} />
                                {t('prescriptionForm2.capturePhoto')}
                              </PhotoButton>
                            </PhotoButtonGroup>
                          </>
                        ) : photoPreview ? (
                          <>
                            <PhotoPreview src={photoPreview} alt={t('prescriptionForm2.prescriptionPreview')} />
                            <PhotoButtonGroup>
                              <PhotoButton type="button" onClick={retakePhoto}>
                                <X size={18} />
                                {t('prescriptionForm2.retake')}
                              </PhotoButton>
                              <PhotoButton type="button" primary onClick={savePhoto} disabled={loading}>
                                <CheckCircle size={18} />
                                {t('prescriptionForm2.savePhoto')}
                              </PhotoButton>
                            </PhotoButtonGroup>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <div>
                        <PhotoSaved>
                          <CheckCircle size={15} />
                          {t('prescriptionForm2.photoSaved')}
                        </PhotoSaved>
                        {prescriptionPhoto && (
                          <PhotoPreview src={prescriptionPhoto} alt={t('prescriptionForm2.savedPrescription')} />
                        )}
                        <PhotoButtonGroup>
                          <PhotoButton type="button" onClick={retakePhoto}>
                            <X size={18} />
                            {t('prescriptionForm2.retake')}
                          </PhotoButton>
                        </PhotoButtonGroup>
                      </div>
                    )}
                  </PhotoSection>
                </div>
              )}
            </RxPhotoCol>
          </RxLayout>

        </FormSection>

        {/* Recommendations Section - shown once correction details are complete */}
        {isOrderSectionReady && (
            <FormSection>
              <SectionTitle>{t('prescriptionForm.orderSelection')}</SectionTitle>
              <div style={{
                background: '#f8fafc',
                padding: '1.5rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                marginBottom: '1rem'
              }}>
                {/* The progress checklist is gone; only the actionable
                    'what is still missing' hints remain, each shown only
                    when it actually applies. */}
                {!isOrderSelectionComplete && selectedOption === 'other' && !selectedOtherProduct && (
                  <OrderHint>{t('prescriptionForm2.pleaseSelectAProductBelow')}</OrderHint>
                )}
                {!isOrderSelectionComplete && selectedOption === 'buyer' && missingBuyerFields.length > 0 && (
                  <OrderHint>{t('prescriptionForm2.missing')} {missingBuyerFields.map(field => field.label).join(', ')}</OrderHint>
                )}
                {!isOrderSelectionComplete && selectedOption === 'professional' && !selectedRecommendation && (
                  <OrderHint>{t('prescriptionForm2.pleaseSelectAProfessionalRecommendation')}</OrderHint>
                )}
                {/* Refusal and pair tabs stay put while a pair's lenses load. */}
                {/* Progressive refusal — sits under the professional
                    recommendation. A 43+ client who declines progressive
                    lenses switches to single vision (far / close up / both,
                    chosen up in the prescription) with a reason recorded. */}
                {(formData.prescriptionType === 'progressive' || formData.progressiveRefused) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.progressiveRefused}
                        onChange={(e) => {
                          const refused = e.target.checked;
                          setSelectedRecommendation(null);
                          setSelectedOtherProduct(null);
                          setOtherDirection(refused ? 'down' : null);
                          setSelectedOption(refused ? 'other' : '');
                          setFormData(prev => ({
                            ...prev,
                            progressiveRefused: refused,
                            prescriptionType: refused ? 'single_vision' : 'progressive',
                            specialCase: false,
                            // Refusing reveals the far / close up chooser up in
                            // the prescription; carry the far values over from the
                            // progressive grid so the order section stays open.
                            singleVisionCorrectionType: refused ? 'far' : '',
                            rightEyeFar: refused && !prev.rightEyeFar.sphere
                              ? { sphere: prev.progressiveRightEyeFar.sphere, cylinder: prev.progressiveRightEyeFar.cylinder, axis: prev.progressiveRightEyeFar.axis, add: '' }
                              : prev.rightEyeFar,
                            leftEyeFar: refused && !prev.leftEyeFar.sphere
                              ? { sphere: prev.progressiveLeftEyeFar.sphere, cylinder: prev.progressiveLeftEyeFar.cylinder, axis: prev.progressiveLeftEyeFar.axis, add: '' }
                              : prev.leftEyeFar,
                            progressiveRefusedReason: refused ? prev.progressiveRefusedReason : ''
                          }));
                        }}
                        style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: '500', color: '#1e293b' }}>{t('prescriptionForm.refusedProgressive')}</span>
                    </label>

                    {formData.progressiveRefused && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
                        <Label style={{ fontWeight: '600', color: '#92400e' }}>
                          {t('prescriptionForm2.whyDoesTheClientNot')}
                        </Label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem', margin: '0.75rem 0' }}>
                          {progressiveRefusalReasonOptions.map((reason) => {
                            const selected = getSelectedProgressiveRefusalReasons().includes(reason);
                            return (
                              <label
                                key={reason}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.65rem',
                                  padding: '0.7rem 0.8rem',
                                  borderRadius: '0.65rem',
                                  border: selected ? '2px solid #b45309' : '1px solid #fbbf24',
                                  background: selected ? '#fff7ed' : '#fffbeb',
                                  color: '#78350f',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  fontFamily: 'inherit'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleProgressiveRefusalReason(reason)}
                                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#b45309', flexShrink: 0 }}
                                />
                                {reason}
                              </label>
                            );
                          })}
                        </div>
                        {!formData.progressiveRefusedReason && (
                          <div style={{ marginTop: '0.4rem', color: '#b45309', fontSize: '0.82rem', fontWeight: 600 }}>
                            {t('prescriptionForm2.atLeastOneReasonIs')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isBothPairs && (
                  <PairSwitch role="tablist" aria-label={t('prescriptionForm2.twoPairsLabel')}>
                    {[['far', t('prescriptionForm2.farLensPair'), farPick], ['near', t('prescriptionForm2.nearLensPair'), nearPick]].map(([pair, label, pick]) => {
                      const lens = lensOfPick(pick);
                      return (
                        <PairTab key={pair} type="button" role="tab" aria-selected={lensPair === pair}
                          $active={lensPair === pair} onClick={() => switchLensPair(pair)}>
                          <span className="label">{label}</span>
                          <span className={lens ? 'pick' : 'pick missing'}>
                            {lens
                              ? `${lens.commercialName || lens.name}${typeof lens.price === 'number' ? ` · ${lens.price.toLocaleString()} DZD` : ''}`
                              : t('prescriptionForm2.pickALens')}
                          </span>
                        </PairTab>
                      );
                    })}
                  </PairSwitch>
                )}

                {loadingRecommendations ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    {t('prescriptionForm2.loadingRecommendations')}
                  </div>
                ) : (
                  <>


                    {(() => {
                      // Show split Up/Down tiles whenever the matrix returns
                      // either list. Otherwise fall back to the single "Other"
                      // tile (legacy data with only a combined list).
                      const hasSplit = lensAlternativesUp.length > 0 || lensAlternativesDown.length > 0;
                      const progressiveWasRefused = formData.progressiveRefused;
                      const gridCols = progressiveWasRefused ? '1fr' : hasSplit ? '1fr 1fr 1fr' : '1fr 1fr';

                      // The professional recommendation, rendered as the first
                      // cell of this row instead of as a separate list above.
                      const recommendationCards = lensRecommendations.length > 0 ? (
                        <>
                      {lensRecommendations.map((rec) => (
                        <div
                          key={rec._id}
                          style={{
                            background: selectedRecommendation?._id === rec._id ? '#f0fdf4' : 'white',
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            border: selectedRecommendation?._id === rec._id ? '2px solid #059669' : '1px solid #e2e8f0',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onClick={() => {
                            setSelectedRecommendation(rec);
                            setSelectedOption('professional');
                          }}
                        >
                          <h6 style={{ margin: '0 0 0.25rem 0', color: '#1e293b', fontSize: '0.875rem', fontWeight: '600' }}>
                            {rec.indexValue ? <span style={{ color: '#7c3aed', marginRight: 6 }}>{rec.indexValue}</span> : null}
                            {rec.commercialName || rec.name}
                          </h6>
                          {/* The power the availability answer refers to. Without
                              it "in stock" is unverifiable at a glance — and it
                              was being claimed for the whole lens model rather
                              than this prescription. */}
                          {(rec.sph != null || rec.cyl != null) && (
                            <p style={{ margin: '0 0 0.35rem 0', color: '#334155', fontSize: '0.75rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              {[
                                rec.sph != null && `SPH ${Number(rec.sph) >= 0 ? '+' : ''}${Number(rec.sph).toFixed(2)}`,
                                rec.cyl != null && `CYL ${Number(rec.cyl) >= 0 ? '+' : ''}${Number(rec.cyl).toFixed(2)}`,
                              ].filter(Boolean).join(' / ')}
                            </p>
                          )}
                          {rec.description && (
                            <p style={{ margin: '0 0 0.5rem 0', color: '#64748b', fontSize: '0.75rem' }}>
                              {rec.description}
                            </p>
                          )}
                          {(() => {
                            // Source of truth for the badge:
                            //   • in stock at central → green "Same day"
                            //   • RX (custom-cut) lens → yellow "~7–10 days"
                            //   • Anything else not in stock → yellow "~3 days"
                            // estimatedDays from the catalog is no longer
                            // used for the badge — the user's spec pins
                            // the wait windows by stock type.
                            if (rec.inStock) {
                              return (
                                <p style={{ margin: '0 0 0.5rem 0', color: '#059669', fontWeight: 600, fontSize: '0.75rem' }}>
                                  {t('prescriptionForm2.timeSameDayInStock')}
                                </p>
                              );
                            }
                            // Stock Int (in-shop) lens that's currently out of stock: it's
                            // restocked internally, so show that plainly instead of a
                            // same-day / 3-day estimate.
                            if (rec.stockType === 'stock_ext_fallback') {
                              return (
                                <p style={{ margin: '0 0 0.5rem 0', color: '#b45309', fontWeight: 600, fontSize: '0.75rem' }}>
                                  {t('prescriptionForm2.stockInternNotInStock')}
                                </p>
                              );
                            }
                            const isRx = rec.stockType === 'rx';
                            return (
                              <p style={{ margin: '0 0 0.5rem 0', color: '#b45309', fontWeight: 600, fontSize: '0.75rem' }}>
                                {t('prescriptionForm2.notInStock')}{rec.defaultDeliveryLabel || (isRx ? t('prescriptionForm2.x710Days') : t('prescriptionForm2.x3Days'))}{isRx ? t('prescriptionForm2.rx') : ''}
                              </p>
                            );
                          })()}
                          <p style={{ margin: '0', fontWeight: '600', color: rec.price != null ? '#059669' : '#94a3b8', fontSize: '0.875rem' }}>
                            {rec.price != null ? `${rec.price.toLocaleString()} DZD` : t('prescriptionForm2.priceToBeSet')}
                          </p>
                        </div>
                      ))}
                        </>
                      ) : null;



                      const otherTile = (direction, list, label, hint, iconColor) => {
                        const active = selectedOption === 'other' && otherDirection === direction;
                        return (
                          <div
                            style={{
                              background: active ? '#f0fdf4' : 'white',
                              padding: '1rem',
                              borderRadius: '0.5rem',
                              border: active ? '2px solid #059669' : '1px solid #e2e8f0',
                              cursor: 'pointer',
                              transition: 'border-color 0.15s ease, background 0.15s ease',
                            }}
                            onClick={() => {
                              setSelectedOption('other');
                              setOtherDirection(direction);
                              // If user previously picked a product, keep it
                              // only if it lives in this direction's list (catalog
                              // picks are always kept).
                              setSelectedOtherProduct(prev => {
                                if (!prev) return prev;
                                if (prev.fromCatalog) return prev;
                                const inHere = list.some(a => a.name === prev.name);
                                return inHere ? prev : null;
                              });
                            }}
                          >
                            <h5 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              <span style={{ color: iconColor, fontWeight: 700 }}>
                                {direction === 'up' ? '▲' : '▼'}
                              </span>
                              {label}
                            </h5>
                            <p style={{ margin: '0 0 0.5rem 0', color: '#64748b', fontSize: '0.8rem' }}>
                              {hint}
                            </p>
                            <p style={{ margin: 0, fontWeight: 600, color: list.length > 0 ? '#1e293b' : '#94a3b8', fontSize: '0.85rem' }}>
                              {loadingAlternatives
                                ? t('prescriptionForm2.loading')
                                : list.length > 0
                                  ? `${list.length} option${list.length === 1 ? '' : 's'}`
                                  : t('prescriptionForm2.notConfigured')}
                            </p>
                          </div>
                        );
                      };

                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1rem', marginBottom: '1rem' }}>
                          {/* The professional recommendation is represented by
                              the detailed card(s) themselves — the old summary
                              tile duplicated the same lens right beside them. */}
                          {!progressiveWasRefused && recommendationCards}

                          {hasSplit ? (
                            <>
                              {!progressiveWasRefused && otherTile('up',   lensAlternativesUp,   'Other (Up)',   'Premium upgrades — more onéreux.',     '#059669')}
                              {otherTile('down', lensAlternativesDown, 'Other (Down)', 'Cheaper alternatives — + économique.', '#ca8a04')}
                            </>
                          ) : (
                            <div
                              style={{
                                background: selectedOption === 'other' ? '#f0fdf4' : 'white',
                                padding: '1rem',
                                borderRadius: '0.5rem',
                                border: selectedOption === 'other' ? '2px solid #059669' : '1px solid #e2e8f0',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setSelectedOption('other');
                                setOtherDirection(null);
                              }}
                            >
                              <h5 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>{t('prescriptionForm.other')}</h5>
                              <p style={{ margin: '0 0 0.5rem 0', color: '#64748b', fontSize: '0.875rem' }}>
                                {t('prescriptionForm2.showAlternativeLensesForThe')}
                              </p>
                              <p style={{ margin: '0', fontWeight: '600', color: '#64748b' }}>
                                {loadingAlternatives
                                  ? t('prescriptionForm2.loading')
                                  : lensAlternatives.length > 0
                                    ? `${lensAlternatives.length} option${lensAlternatives.length === 1 ? '' : 's'} available`
                                    : t('prescriptionForm2.enterPrescriptionFirst')}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}

                {selectedOption === 'other' && (() => {
                  const hasSplit = lensAlternativesUp.length > 0 || lensAlternativesDown.length > 0;
                  const progressiveWasRefused = formData.progressiveRefused;
                  // Effective direction defaults to whichever list is populated
                  // when the panel is opened without an explicit choice.
                  const effectiveDirection = progressiveWasRefused
                    ? 'down'
                    : otherDirection
                    ?? (hasSplit
                        ? (lensAlternativesUp.length > 0 ? 'up' : 'down')
                        : null);
                  const visibleList = !hasSplit
                    ? lensAlternatives
                    : effectiveDirection === 'up'
                      ? lensAlternativesUp
                      : lensAlternativesDown;
                  const directionLabel = effectiveDirection === 'up'
                    ? 'Premium upgrades (Up)'
                    : effectiveDirection === 'down'
                      ? 'Cheaper alternatives (Down)'
                      : 'Other lens options';

                  return (
                    <div style={{
                      background: 'white',
                      padding: '1.5rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0',
                      marginTop: '1rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.25rem', color: '#1e293b' }}>{directionLabel}</h4>
                          <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
                            {t('prescriptionForm2.alternativesForTheSameLens')}
                          </p>
                        </div>
                        {selectedRecommendation?.groupPair && (
                          <div style={{
                            padding: '0.3rem 0.6rem', background: '#eef2ff', color: '#3730a3',
                            borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
                          }}>
                            {t('prescriptionForm2.group')} {selectedRecommendation.groupPair.join(' & ')} · {selectedRecommendation.sphereSign === 'plus' ? '(+)' : selectedRecommendation.sphereSign === 'minus' ? '(−)' : '—'}
                          </div>
                        )}
                      </div>

                      {hasSplit && !progressiveWasRefused && (
                        <div style={{ display: 'inline-flex', gap: '0.4rem', padding: '0.25rem', background: '#f1f5f9', borderRadius: '9999px', marginBottom: '1rem' }}>
                          {[
                            { id: 'up',   label: '▲ Up — premium',     count: lensAlternativesUp.length,   active: '#059669' },
                            { id: 'down', label: '▼ Down — économique', count: lensAlternativesDown.length, active: '#ca8a04' },
                          ].map(tab => {
                            const on = effectiveDirection === tab.id;
                            return (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => {
                                  setOtherDirection(tab.id);
                                  setSelectedOtherProduct(prev => {
                                    if (!prev) return prev;
                                    if (prev.fromCatalog) return prev;
                                    const list = tab.id === 'up' ? lensAlternativesUp : lensAlternativesDown;
                                    const inHere = list.some(a => a.name === prev.name);
                                    return inHere ? prev : null;
                                  });
                                }}
                                style={{
                                  padding: '0.4rem 0.9rem',
                                  borderRadius: '9999px',
                                  border: 'none',
                                  background: on ? 'white' : 'transparent',
                                  color: on ? tab.active : '#475569',
                                  fontWeight: on ? 700 : 500,
                                  fontSize: '0.82rem',
                                  cursor: 'pointer',
                                  boxShadow: on ? '0 1px 3px rgba(15,23,42,0.12)' : 'none',
                                  transition: 'background 0.15s ease, color 0.15s ease',
                                }}
                              >
                                {tab.label} · {tab.count}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Extra path: pick any lens straight from the full catalog. */}
                      <div style={{ marginBottom: '1rem' }}>
                        <button
                          type="button"
                          onClick={() => openCatalogBrowser(false)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                            padding: '0.5rem 0.95rem', borderRadius: '9999px',
                            border: '1px dashed #6366f1', background: '#eef2ff', color: '#4338ca',
                            fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                          }}
                        >
                          <Eye size={15} /> {t('prescriptionForm2.chooseFromFullCatalog')}
                        </button>
                        {selectedOtherProduct?.fromCatalog && (
                          <span style={{ marginLeft: '0.6rem', fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}>
                            {t('prescriptionForm2.selected')} {selectedOtherProduct.commercialName || selectedOtherProduct.name}
                            {selectedOtherProduct.price != null ? ` · ${selectedOtherProduct.price.toLocaleString()} DZD` : ''}
                            {(() => {
                              const avail = lensAvailability(selectedOtherProduct);
                              return avail ? <span style={{ color: avail.color }}> · {avail.text}</span> : null;
                            })()}
                          </span>
                        )}
                      </div>


                      {loadingAlternatives ? (
                        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '1.5rem 0' }}>
                          {t('prescriptionForm2.loadingAlternatives')}
                        </p>
                      ) : visibleList.length === 0 ? (
                        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '1.5rem 0' }}>
                          {selectedRecommendation
                            ? hasSplit
                              ? `No ${effectiveDirection === 'up' ? 'premium upgrades' : 'cheaper alternatives'} configured for this group.`
                              : t('prescriptionForm2.noAlternativesConfiguredForThis')
                            : t('prescriptionForm2.enterThePrescriptionFirstSo')}
                        </p>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                          {visibleList.map((alt, idx) => {
                            const isSelected = selectedOtherProduct?.name === alt.name;
                            return (
                              <div
                                key={`${alt.name}-${idx}`}
                                onClick={() => setSelectedOtherProduct(isSelected ? null : alt)}
                                style={{
                                  padding: '0.9rem',
                                  borderRadius: '0.5rem',
                                  border: isSelected ? '2px solid #059669' : '1px solid #e2e8f0',
                                  background: isSelected ? '#f0fdf4' : '#f8fafc',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                                  <h5 style={{ margin: 0, color: '#1e293b', fontSize: '0.92rem' }}>{alt.commercialName || alt.name}</h5>
                                  {isSelected && <span style={{ color: '#059669', fontSize: '1.2rem' }}>&#10003;</span>}
                                </div>
                                <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#059669', fontSize: '0.88rem' }}>
                                  {typeof alt.price === 'number' && alt.price > 0
                                    ? `${alt.price.toLocaleString()} DZD`
                                    : <span style={{ color: '#94a3b8', fontWeight: 500 }}>{t('prescriptionForm2.priceToBeSet')}</span>}
                                </p>
                                {(() => {
                                  const avail = lensAvailability(alt);
                                  return avail ? (
                                    <p style={{ margin: 0, fontSize: '0.78rem', color: avail.color, fontWeight: 600 }}>
                                      {avail.text}
                                    </p>
                                  ) : null;
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {isBothPairs && (() => {
                  const far = lensOfPick(farPick);
                  const near = lensOfPick(nearPick);
                  const priceOf = (lens) => (typeof lens?.price === 'number' ? lens.price : 0);
                  return (
                    <div style={{ background: '#1e293b', color: 'white', padding: '1rem', borderRadius: '0.5rem', marginTop: '1rem' }}>
                      <h4 style={{ margin: '0 0 0.6rem 0', textAlign: 'center' }}>{t('prescriptionForm.totalEstimatedCost')}</h4>
                      {[[t('prescriptionForm2.farLensPair'), far], [t('prescriptionForm2.nearLensPair'), near]].map(([label, lens]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.9rem', padding: '0.2rem 0' }}>
                          <span style={{ opacity: 0.8 }}>{label} · {lens ? (lens.commercialName || lens.name) : t('prescriptionForm2.pickALens')}</span>
                          <span>{lens ? `${priceOf(lens).toLocaleString()} DZD` : '—'}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: '0.5rem', paddingTop: '0.5rem', fontSize: '1.25rem', fontWeight: 700 }}>
                        <span>{t('prescriptionForm2.totalBothPairs')}</span>
                        <span>{(priceOf(far) + priceOf(near)).toLocaleString()} DZD</span>
                      </div>
                    </div>
                  );
                })()}
                {!isBothPairs && selectedOption && (
                  <div style={{
                    background: '#1e293b',
                    color: 'white',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    marginTop: '1rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>{t('prescriptionForm.totalEstimatedCost')}</h4>
                    <p style={{ margin: '0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {selectedOption === 'professional'
                        ? selectedRecommendation
                          ? typeof selectedRecommendation.price === 'number' && selectedRecommendation.price > 0
                            ? `${selectedRecommendation.price.toLocaleString()} DZD`
                            : t('prescriptionForm2.priceToBeSet')
                          : t('prescriptionForm2.selectARecommendation')
                        : selectedOption === 'other' && selectedOtherProduct
                          ? typeof selectedOtherProduct.price === 'number' && selectedOtherProduct.price > 0
                            ? `${selectedOtherProduct.price.toLocaleString()} DZD`
                            : t('prescriptionForm2.priceToBeSet')
                          : t('prescriptionForm2.selectALens')}
                    </p>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', opacity: '0.8' }}>
                      {selectedOption === 'professional'
                        ? selectedRecommendation
                          ? `${selectedRecommendation.commercialName || selectedRecommendation.name} - ${selectedRecommendation.description || 'Professional Recommendation'}`
                          : t('prescriptionForm2.professionalRecommendation')
                        : selectedOption === 'other' && selectedOtherProduct
                          ? (selectedOtherProduct.commercialName || selectedOtherProduct.name)
                          : t('prescriptionForm2.otherSelectALensAbove')
                      }
                    </p>
                    {/* The delivery promise belongs on the lens being quoted,
                        not only on the ones being compared. */}
                    {(() => {
                      const chosen = selectedOption === 'professional' ? selectedRecommendation : selectedOtherProduct;
                      const avail = lensAvailability(chosen);
                      if (!avail) return null;
                      const power = [
                        chosen.sph != null && `SPH ${Number(chosen.sph) >= 0 ? '+' : ''}${Number(chosen.sph).toFixed(2)}`,
                        chosen.cyl != null && `CYL ${Number(chosen.cyl) >= 0 ? '+' : ''}${Number(chosen.cyl).toFixed(2)}`,
                      ].filter(Boolean).join(' / ');
                      return (
                        <div style={{
                          marginTop: '0.6rem',
                          paddingTop: '0.6rem',
                          borderTop: '1px solid rgba(255,255,255,0.15)',
                        }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.2rem 0.6rem',
                            borderRadius: 999,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            // Available-but-elsewhere is its own state: it is a
                            // yes, so not red, but it is not "reach behind you".
                            background: (avail.ok && !avail.warn) ? 'rgba(5,150,105,0.22)' : 'rgba(180,83,9,0.25)',
                            color: (avail.ok && !avail.warn) ? '#6ee7b7' : '#fcd34d',
                          }}>
                            {avail.text}
                          </span>
                          {power && (
                            <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                              {t('prescriptionForm2.forPower')} {power}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </FormSection>
          )}

        <ButtonGroup>
          <SaveButton
            type="submit"
            disabled={loading || !isOrderSelectionComplete || !photoSaved}
            style={{
              opacity: (!isOrderSelectionComplete || !photoSaved) ? 0.5 : 1,
              cursor: (!isOrderSelectionComplete || !photoSaved) ? 'not-allowed' : 'pointer'
            }}
          >
            <Eye size={20} />
            {!photoSaved ? t('prescriptionForm2.saveThePrescriptionPhotoFirst') : loading ? t('prescriptionForm2.saving') : selectedOption === 'professional' ? t('prescriptionForm2.orderProfessionalRecommendation') : selectedOption === 'other' ? (selectedOtherProduct ? `Order ${selectedOtherProduct.commercialName || selectedOtherProduct.name}` : t('prescriptionForm2.selectAProduct')) : selectedOption === 'buyer' ? t('prescriptionForm2.orderSelection') : t('prescriptionForm2.selectOrderTypeFirst')}
          </SaveButton>
        </ButtonGroup>
      </Form>

      {/* Modern Alert Modal */}
      {alertMessage && (
        <AlertModalOverlay onClick={closeAlert}>
          <AlertModalContent onClick={(e) => e.stopPropagation()}>
            <AlertModalHeader type={alertType}>
              <AlertIcon type={alertType}>
                {alertType === 'success' ? (
                  <CheckCircle size={24} />
                ) : (
                  <AlertCircle size={24} />
                )}
              </AlertIcon>
              <AlertTitle type={alertType}>
                {alertType === 'success' ? t('prescriptionForm2.success') : alertType === 'warning' ? t('prescriptionForm2.warning') : t('prescriptionForm2.error')}
              </AlertTitle>
            </AlertModalHeader>
            <AlertModalBody>
              <AlertMessage>{alertMessage}</AlertMessage>
              <AlertButton type={alertType} onClick={closeAlert}>{t('common.ok')}</AlertButton>
            </AlertModalBody>
          </AlertModalContent>
        </AlertModalOverlay>
      )}

      {/* Duplicate Client Modal */}
      {showDuplicateModal && existingPrescriptionData && (
        <DuplicateModalOverlay onClick={closeDuplicateModal}>
          <DuplicateModalContent onClick={(e) => e.stopPropagation()}>
            <DuplicateModalHeader>
              <DuplicateModalTitle>
                <AlertCircle size={24} />
                {t('prescriptionForm2.existingClientFound')}
              </DuplicateModalTitle>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#92400e' }}
                onClick={closeDuplicateModal}
              />
            </DuplicateModalHeader>
            <DuplicateModalBody>
              <p style={{ marginBottom: '1.5rem', color: '#64748b' }}>
                {duplicateMatchType === 'identity'
                  ? t('prescriptionForm2.aClientWithTheSame')
                  : t('prescriptionForm2.aClientWithThisPhone')}
              </p>

              {existingPrescriptionData.map((client, index) => (
                <OldCorrectionSection key={index}>
                  <OldCorrectionTitle>
                    {client.firstName} {client.lastName}
                    {client.visitDate && ` - Last Visit: ${formatModalDate(client.visitDate)}`}
                  </OldCorrectionTitle>
                  <CorrectionInfo>
                    <CorrectionInfoItem>
                      <span>{t('prescriptionForm2.address2')}</span>
                      <span>{client.address || 'N/A'}</span>
                    </CorrectionInfoItem>
                    <CorrectionInfoItem>
                      <span>{t('prescriptionForm2.phone')}</span>
                      <span>{client.phone || client.phoneNumber || 'N/A'}</span>
                    </CorrectionInfoItem>
                    <CorrectionInfoItem>
                      <span>{t('prescriptionForm2.visits')}</span>
                      <span>{duplicateHistory?.visitors?.length || duplicateHistory?.profile?.visitCount || 'N/A'}</span>
                    </CorrectionInfoItem>
                  </CorrectionInfo>

                  <DuplicateHistoryGrid>
                    <DuplicateHistoryCard>
                      <DuplicateHistoryTitle>
                        <Calendar size={16} />
                        {t('prescriptionForm2.previousVisits')}
                      </DuplicateHistoryTitle>
                      {duplicateHistoryLoading ? (
                        <EmptyHistory>{t('prescriptionForm2.loadingVisitHistory')}</EmptyHistory>
                      ) : duplicateHistory?.visitors?.length ? (
                        <DuplicateHistoryList>
                          {duplicateHistory.visitors.slice(0, 3).map((visit) => (
                            <DuplicateHistoryItem key={visit._id}>
                              <HistoryMeta>{formatModalDate(visit.visitDate)}</HistoryMeta>
                              {visit.searchMotive || 'Visit recorded'}
                              {visit.salesPerson && (
                                <div>{t('prescriptionForm2.servedBy')} {visit.salesPerson.firstName} {visit.salesPerson.lastName}</div>
                              )}
                            </DuplicateHistoryItem>
                          ))}
                        </DuplicateHistoryList>
                      ) : (
                        <EmptyHistory>{t('prescriptionForm2.noPreviousVisitsFound')}</EmptyHistory>
                      )}
                    </DuplicateHistoryCard>

                    <DuplicateHistoryCard>
                      <DuplicateHistoryTitle>
                        <Eye size={16} />
                        {t('prescriptionForm2.previousCorrections')}
                      </DuplicateHistoryTitle>
                      {duplicateHistoryLoading ? (
                        <EmptyHistory>{t('prescriptionForm2.loadingCorrections')}</EmptyHistory>
                      ) : duplicateHistory?.prescriptions?.length ? (
                        <DuplicateHistoryList>
                          {duplicateHistory.prescriptions.slice(0, 3).map((rx) => (
                            <DuplicateHistoryItem key={rx._id}>
                              <HistoryMeta>
                                {formatModalDate(rx.prescriptionDate || rx.createdAt)} · {(rx.prescriptionType || 'Prescription').replace(/_/g, ' ')}
                              </HistoryMeta>
                              {getCorrectionSummary(rx)}
                              {rx.prescriptionPhoto ? (
                                <HistoryPhotoButton
                                  type="button"
                                  onClick={() => setHistoryPrescriptionPhoto({
                                    src: rx.prescriptionPhoto,
                                    title: `Prescription photo - ${formatModalDate(rx.prescriptionDate || rx.createdAt)}`
                                  })}
                                >
                                  <Camera size={14} />
                                  {t('prescriptionForm2.viewPrescriptionPhoto')}
                                </HistoryPhotoButton>
                              ) : (
                                <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                  <Camera size={13} /> {t('prescriptionForm2.noPhotoOnFile')}
                                </span>
                              )}
                            </DuplicateHistoryItem>
                          ))}
                        </DuplicateHistoryList>
                      ) : (
                        <EmptyHistory>{t('prescriptionForm2.noPreviousCorrectionsFound')}</EmptyHistory>
                      )}
                    </DuplicateHistoryCard>

                    <DuplicateHistoryCard>
                      <DuplicateHistoryTitle>
                        <Save size={16} />
                        {t('prescriptionForm2.purchaseHistory')}
                      </DuplicateHistoryTitle>
                      {duplicateHistoryLoading ? (
                        <EmptyHistory>{t('prescriptionForm2.loadingPurchases')}</EmptyHistory>
                      ) : duplicateHistory?.orders?.length ? (
                        <DuplicateHistoryList>
                          {duplicateHistory.orders.slice(0, 3).map((order) => (
                            <DuplicateHistoryItem key={order._id}>
                              <HistoryMeta>
                                {formatModalDate(order.createdAt)} · {(order.status || 'order').replace(/_/g, ' ')}
                              </HistoryMeta>
                              {getOrderItemsSummary(order)}
                              <div>{order.total ? `${order.total.toLocaleString()} DZD` : t('prescriptionForm2.totalNotSaved')}</div>
                            </DuplicateHistoryItem>
                          ))}
                        </DuplicateHistoryList>
                      ) : (
                        <EmptyHistory>{t('prescriptionForm2.noPurchaseHistoryFound')}</EmptyHistory>
                      )}
                    </DuplicateHistoryCard>
                  </DuplicateHistoryGrid>
                </OldCorrectionSection>
              ))}

              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <p style={{ margin: 0, color: '#166534', fontWeight: 500 }}>
                  {t('prescriptionForm2.wouldYouLikeToStart')}
                </p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#15803d' }}>
                  {t('prescriptionForm2.thisWillPreFillTheir')}
                </p>
              </div>

              <DuplicateModalActions>
                <DuplicateButton onClick={closeDuplicateModal}>
                  {t('prescriptionForm2.cancel')}
                </DuplicateButton>
                <DuplicateButton primary onClick={handleUseExistingClient}>
                  {t('prescriptionForm2.startNewVisit')}
                </DuplicateButton>
              </DuplicateModalActions>
            </DuplicateModalBody>
          </DuplicateModalContent>
        </DuplicateModalOverlay>
      )}
      {historyPrescriptionPhoto && (
        <HistoryPhotoOverlay onClick={() => setHistoryPrescriptionPhoto(null)}>
          <HistoryPhotoModal onClick={(e) => e.stopPropagation()}>
            <HistoryPhotoHeader>
              <span>{historyPrescriptionPhoto.title || 'Previous prescription photo'}</span>
              <X
                size={24}
                style={{ cursor: 'pointer', color: '#475569' }}
                onClick={() => setHistoryPrescriptionPhoto(null)}
              />
            </HistoryPhotoHeader>
            <HistoryPhotoImage src={historyPrescriptionPhoto.src} alt={t('prescriptionForm2.previousPrescription2')} />
          </HistoryPhotoModal>
        </HistoryPhotoOverlay>
      )}
    </Container >
  );
};

export default PrescriptionForm;
